/**
 * Frontend export utilities for artifact/chat content.
 * Used for PDF, Word, and PPT export via html2canvas + jsPDF/docx/pptxgenjs.
 * Excel (full dataset) is often handled via APIs (parent onArtifactExport). Table “current view”
 * exports in Ask Asper agentic use the visualization payload (`exportAgenticTableVizToExcel`).
 */
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import {
    Document as DocxDocument,
    Packer,
    Paragraph,
    ImageRun,
    HeadingLevel,
    AlignmentType,
    TextRun,
    PageBreak,
    ExternalHyperlink,
} from 'docx';
import { saveAs } from 'file-saver';
import PptxGenJS from 'pptxgenjs';
import {
    formatTablePreviewSummary,
    resolveTablePreviewStatsForExport,
    type TablePreviewStats,
} from '../components/molecules/DataTable/tablePreviewStats';
import { buildAskAsperExportBasename } from './exportFilename';

const MARGIN = 20;
/**
 * Vertical gap (mm) inserted between captured elements in PDF.
 * Kept small so cards don’t float in unnecessary whitespace; previously 10 mm
 * which left a visible gap between every chart/table/methodology block.
 */
const SPACING = 6;
const CANVAS_SCALE = 2;
const PPT_SLIDE_TITLE_ATTR = 'data-export-slide-title';
const PPT_SLIDE_TITLE_CLASS = 'ppt-export-slide-title';
const RIGHT_PANEL_BLOCK_ATTR = 'data-right-panel-block';
const PPT_METHODOLOGY_ATTR = 'data-export-methodology';
const METHODOLOGY_CAPTURE_ATTR = 'data-export-methodology-capture';

type PptMethodologyExportPayload = {
    title?: string;
    sections?: Array<{ title: string; items: string[] }>;
    formula?: string | string[];
};

/**
 * Single source of truth for the font used in exports.
 *
 * Aligned with jsPDF's built-in `helvetica` so that:
 * - The native "Question" preamble and headings emitted directly by jsPDF
 *   render in the same family as the rasterised "Summary" / "Top Product
 *   Groups" / chart text captured via html2canvas.
 * - DOCX `TextRun({ font })` and PPT `addText({ fontFace })` use the same
 *   name, so Word / PowerPoint render their native text in the same family
 *   as the captured images. (On Windows, Word/PowerPoint substitute
 *   Helvetica with Arial automatically — they're metrically identical.)
 *
 * Previously this stack started with `Calibri`, but Calibri ships with
 * Microsoft Office only and is missing on most macOS/Linux machines. When
 * Calibri was unavailable the cloned DOM fell back to `Open Sans` (the
 * app's UI font), which is visibly heavier than jsPDF's helvetica — so the
 * "Question" label and the "Summary" header looked like two different
 * fonts on the same page.
 */
const EXPORT_FONT_PRIMARY = 'Helvetica';
const EXPORT_FONT_FAMILY_CSS = "'Helvetica', 'Arial', sans-serif";

export type ArtifactPdfDocxExportOptions = {
    filename?: string;
    title?: string;
    /** User chat query for this export scope (fallback when not on `[data-right-panel-block]`). */
    userQuery?: string;
};

function readUserQueryFromRightPanelBlock(block: HTMLElement | null): string | undefined {
    if (!block) return undefined;
    const raw = block.getAttribute('data-user-query')?.trim();
    if (!raw) return undefined;
    try {
        return decodeURIComponent(raw);
    } catch {
        return raw;
    }
}

function resolveUserQueryForExportPart(
    part: HTMLElement,
    options?: Pick<ArtifactPdfDocxExportOptions, 'userQuery'>,
): string | undefined {
    const block = part.closest<HTMLElement>(`[${RIGHT_PANEL_BLOCK_ATTR}]`);
    const fromDom = readUserQueryFromRightPanelBlock(block);
    if (fromDom) return fromDom;
    return options?.userQuery?.trim();
}

/**
 * Make antd `<Table>` column headers readable in exports without changing
 * the table's visible width.
 *
 * `DataTable` renders column titles as either `<span style="white-space:nowrap">`
 * (≤30 chars) or `<span class="truncate">` (>30 chars, with overflow:hidden
 * and text-overflow:ellipsis). The header `<th>` itself also has
 * `white-space:nowrap`. Combined with antd's horizontal scroll
 * (`scroll={{ x: 'max-content' }}`) this means long column names either get
 * ellipsised inside their cell or sit inside a cell that scrolls behind the
 * panel edge — producing the trimmed names ("est Volume (Eq Units)",
 * "Asper POS Forecast Latest Valu") seen in the screenshots.
 *
 * For exports we don't want to force the full table to render; we only want
 * the visible portion. Letting header text wrap inside its cell is enough:
 * the column auto-sizes around the wrapped header instead of relying on a
 * single-line cell that overflows the viewport.
 */
function relaxAntdTableHeadersForExport(clonedDocument: globalThis.Document): void {
    // Keep `<th>` widths stable (synced from body in {@link syncAntdTableHeaderBodyForExport}).
    // Only relax inner title nodes so long headers wrap inside the fixed column width.
    const headerTextNodes = clonedDocument.querySelectorAll<HTMLElement>(
        '.ant-table-thead .truncate, .ant-table-thead span, .ant-table-header .truncate, .ant-table-header span',
    );
    headerTextNodes.forEach((el) => {
        if (el.closest('th, .ant-table-cell')) {
            el.style.display = 'block';
            el.style.maxWidth = '100%';
            el.style.whiteSpace = 'normal';
            el.style.overflowWrap = 'anywhere';
            el.style.overflow = 'visible';
            el.style.textOverflow = 'clip';
        }
    });
}

const EXPORT_TABLE_FLATTENED_ATTR = 'data-export-table-flattened';

/**
 * Ant Design horizontal-scroll tables render header and body as two separate
 * `<table>` elements kept in sync via JS. html2canvas does not re-run that sync,
 * which misaligns header/body grid lines. Merge header into the body table in
 * the clone so one table drives column layout for the capture.
 */
function flattenAntdScrollTableForExport(clonedDocument: globalThis.Document): void {
    const containers = clonedDocument.querySelectorAll<HTMLElement>('.ant-table-container');
    containers.forEach((container) => {
        if (container.getAttribute(EXPORT_TABLE_FLATTENED_ATTR) === 'true') return;

        const headerHolder = container.querySelector<HTMLElement>('.ant-table-header');
        const bodyHolder = container.querySelector<HTMLElement>('.ant-table-body');
        const headerTable = container.querySelector<HTMLTableElement>('.ant-table-header table');
        const bodyTable = container.querySelector<HTMLTableElement>('.ant-table-body table');
        if (!headerHolder || !bodyTable || !headerTable) return;

        const headerThead = headerTable.querySelector('thead');
        const bodyTbody = bodyTable.querySelector('tbody');
        if (!headerThead || !bodyTbody) return;
        if (bodyTable.querySelector('thead')) return;

        const headerColgroup = headerTable.querySelector('colgroup');
        const bodyColgroup = bodyTable.querySelector('colgroup');
        if (headerColgroup) {
            const cloneColgroup = headerColgroup.cloneNode(true) as HTMLElement;
            if (bodyColgroup) {
                bodyColgroup.replaceWith(cloneColgroup);
            } else {
                bodyTable.insertBefore(cloneColgroup, bodyTbody);
            }
        }

        bodyTable.insertBefore(headerThead.cloneNode(true), bodyTbody);

        headerHolder.style.display = 'none';
        headerHolder.style.height = '0';
        headerHolder.style.minHeight = '0';
        headerHolder.style.overflow = 'hidden';
        headerHolder.style.pointerEvents = 'none';

        bodyTable.style.tableLayout = 'auto';
        bodyTable.style.width = `${bodyTable.scrollWidth}px`;

        if (bodyHolder) {
            bodyHolder.style.overflow = 'auto';
        }

        container.setAttribute(EXPORT_TABLE_FLATTENED_ATTR, 'true');
    });
}

/**
 * Fallback when flatten did not apply (non-scroll tables): sync header/body widths.
 */
function syncAntdTableHeaderBodyForExport(clonedDocument: globalThis.Document): void {
    const view = clonedDocument.defaultView;
    const containers = clonedDocument.querySelectorAll<HTMLElement>('.ant-table-container');
    containers.forEach((container) => {
        if (container.getAttribute(EXPORT_TABLE_FLATTENED_ATTR) === 'true') return;

        const bodyHolder = container.querySelector<HTMLElement>('.ant-table-body');
        const headerHolder = container.querySelector<HTMLElement>('.ant-table-header');
        const bodyTable = container.querySelector<HTMLTableElement>('.ant-table-body table');
        const headerTable = container.querySelector<HTMLTableElement>('.ant-table-header table');
        if (!bodyTable || !headerTable) return;

        const bodyRow = bodyTable.querySelector('tbody tr');
        const headerCells = headerTable.querySelectorAll<HTMLElement>('thead th, thead .ant-table-cell');
        const bodyCells = bodyRow?.querySelectorAll<HTMLElement>('td') ?? [];

        bodyTable.style.tableLayout = 'fixed';
        headerTable.style.tableLayout = 'fixed';

        const tableWidth = bodyTable.offsetWidth;
        if (tableWidth > 0) {
            const w = `${tableWidth}px`;
            bodyTable.style.width = w;
            headerTable.style.width = w;
        }

        const bodyCols = bodyTable.querySelectorAll<HTMLElement>('colgroup col');
        const headerCols = headerTable.querySelectorAll<HTMLElement>('colgroup col');
        bodyCols.forEach((col, index) => {
            const headerCol = headerCols[index];
            if (!headerCol) return;
            const width = col.style.width || col.getAttribute('width');
            if (width) headerCol.style.width = typeof width === 'string' ? width : `${width}px`;
        });

        headerCells.forEach((th, index) => {
            const td = bodyCells[index];
            if (!td) return;
            const tdRect = td.getBoundingClientRect();
            const width = tdRect.width || td.offsetWidth;
            if (width <= 0) return;
            const w = `${Math.round(width)}px`;
            th.style.width = w;
            th.style.minWidth = w;
            th.style.maxWidth = w;
            th.style.boxSizing = 'border-box';

            if (view) {
                const tdStyle = view.getComputedStyle(td);
                th.style.paddingTop = tdStyle.paddingTop;
                th.style.paddingRight = tdStyle.paddingRight;
                th.style.paddingBottom = tdStyle.paddingBottom;
                th.style.paddingLeft = tdStyle.paddingLeft;
            }
        });

        if (bodyHolder && headerHolder) {
            headerHolder.scrollLeft = bodyHolder.scrollLeft;
            headerHolder.style.overflow = 'hidden';
        }
    });
}

/**
 * Stop the chart legend's visible row from clipping its chips on export.
 *
 * `ChartLegendOverflow` measures chip widths against the row width in
 * `useLayoutEffect` and locks `visibleCount` to whatever fits in the live
 * DOM. The row itself has `overflow: hidden` so the rightmost chip never
 * spills past its right edge in the UI, and a `+N More` button hides the
 * rest.
 *
 * `html2canvas` clones the DOM but does not re-run React effects, so
 * `visibleCount` stays whatever it was on screen. We then swap the font
 * stack to Helvetica via {@link applyFontConsistencyToClone} so captured
 * text matches native PDF/DOCX/PPT text — and Helvetica's metrics differ
 * slightly from Open Sans, so chips that fit the row exactly on screen
 * can now overrun the row by a couple of pixels in the clone. The
 * `overflow: hidden` then slices those few pixels off, which is what
 * crops the rounded marker / chip edge in the exported PNG.
 *
 * The `+N More` count is intentionally preserved (matches the on-screen
 * legend); we only relax the clipping rectangle so the chips that the
 * user *can* see in the UI also render in full in the export. The legend
 * section wrapper has `px-5` (20 px) of horizontal padding, which absorbs
 * any sub-pixel spill without touching the chart card border.
 */
function relaxChartLegendForExport(clonedDocument: globalThis.Document): void {
    const legendRows = clonedDocument.querySelectorAll<HTMLElement>(
        '[data-chart-legend-overflow-row]',
    );
    legendRows.forEach((row) => {
        row.style.overflow = 'visible';
    });
}

/**
 * Chart filter bars collapse extra rows in the UI (`display: none`) so the
 * “More filters” toggle can reveal them. html2canvas clones that collapsed
 * state, so exports would miss filter values beyond the first row.
 *
 * ChartFilterBar styles use `overflow: hidden !important` on ant Select for
 * ellipsis in the UI; combined with a fixed selector height and the Helvetica
 * swap in {@link applyFontConsistencyToClone}, html2canvas clips the bottom of
 * filter values. Negative margins and `items-end` on the filter grid can also
 * clip the tops of column labels ("Month", "Primary Axis", etc.). The filter
 * grid always reserves `maxVisible` (4) columns even when fewer filters are
 * shown, leaving each control at ~25% width — combined with nowrap ellipsis
 * styles, long values like "Havahart Shipment Volume Units" clip in export.
 * Run this after font normalisation and override with inline `!important`
 * rules so export captures full label + value text.
 */
function setExportStyleImportant(element: HTMLElement, property: string, value: string): void {
    element.style.setProperty(property, value, 'important');
}

/** Let filter values wrap instead of ellipsis-clipping during html2canvas capture. */
function relaxChartFilterSelectTextForExport(element: HTMLElement): void {
    setExportStyleImportant(element, 'overflow', 'visible');
    setExportStyleImportant(element, 'white-space', 'normal');
    setExportStyleImportant(element, 'word-break', 'break-word');
    setExportStyleImportant(element, 'overflow-wrap', 'anywhere');
    setExportStyleImportant(element, 'text-overflow', 'clip');
    setExportStyleImportant(element, 'height', 'auto');
    setExportStyleImportant(element, 'min-width', '0');
    setExportStyleImportant(element, 'max-width', '100%');
    element.style.lineHeight = '22px';
    element.style.minHeight = '22px';
}

function prepareChartFiltersForExport(clonedDocument: globalThis.Document): void {
    const root = clonedDocument.documentElement;
    if (root.getAttribute('data-export-chart-filters-prepared') !== 'true') {
        root.setAttribute('data-export-chart-filters-prepared', 'true');
        const style = clonedDocument.createElement('style');
        style.textContent = `
            [data-export-chart-filters-prepared] .asper-chart-filter-select.ant-select,
            [data-export-chart-filters-prepared] .asper-chart-filter-select.ant-select .ant-select-selector,
            [data-export-chart-filters-prepared] .asper-chart-filter-select.ant-select .ant-select-selection-wrap,
            [data-export-chart-filters-prepared] .asper-chart-filter-select.ant-select .ant-select-selection-overflow,
            [data-export-chart-filters-prepared] .asper-chart-filter-select.ant-select .ant-select-selection-overflow-item,
            [data-export-chart-filters-prepared] .asper-chart-filter-select.ant-select .ant-select-selection-item,
            [data-export-chart-filters-prepared] .asper-chart-filter-select.ant-select .ant-select-selection-placeholder,
            [data-export-chart-filters-prepared] .asper-chart-filter-select.ant-select .asper-chart-filter-single-selected-value,
            [data-export-chart-filters-prepared] .asper-chart-filter-select.ant-select-multiple .ant-select-selection-overflow-item > span:first-child {
                overflow: visible !important;
            }
            [data-export-chart-filters-prepared] .asper-chart-filter-select.ant-select .ant-select-selector {
                height: auto !important;
                min-height: 32px !important;
                padding-top: 5px !important;
                padding-bottom: 5px !important;
                padding-right: 11px !important;
                display: flex !important;
                align-items: flex-start !important;
                overflow: visible !important;
            }
            [data-export-chart-filters-prepared] .asper-chart-filter-select.ant-select-single:not(.ant-select-customize-input)
                .ant-select-selection-item,
            [data-export-chart-filters-prepared] .asper-chart-filter-select.ant-select-single:not(.ant-select-customize-input)
                .ant-select-selection-placeholder,
            [data-export-chart-filters-prepared] .asper-chart-filter-select.ant-select .ant-select-selection-item,
            [data-export-chart-filters-prepared] .asper-chart-filter-select.ant-select .ant-select-selection-placeholder,
            [data-export-chart-filters-prepared] .asper-chart-filter-select.ant-select .asper-chart-filter-single-selected-value,
            [data-export-chart-filters-prepared] .asper-chart-filter-select.ant-select-multiple
                .ant-select-selection-overflow-item > span:first-child,
            [data-export-chart-filters-prepared] .asper-chart-filter-select.ant-select-multiple
                .ant-select-selection-overflow-item:first-child {
                overflow: visible !important;
                white-space: normal !important;
                word-break: break-word !important;
                overflow-wrap: anywhere !important;
                text-overflow: clip !important;
                min-width: 0 !important;
                max-width: 100% !important;
            }
            [data-export-chart-filters-prepared] .asper-chart-filter-select.ant-select-multiple .ant-select-selection-overflow {
                flex-wrap: wrap !important;
                overflow: visible !important;
            }
            [data-export-chart-filters-prepared] .asper-chart-filter-select.ant-select .ant-select-selection-item,
            [data-export-chart-filters-prepared] .asper-chart-filter-select.ant-select .ant-select-selection-placeholder,
            [data-export-chart-filters-prepared] .asper-chart-filter-select.ant-select .asper-chart-filter-single-selected-value {
                line-height: 22px !important;
                min-height: 22px !important;
                height: auto !important;
            }
            [data-export-chart-filters-prepared] .asper-chart-filter-select.ant-select .ant-select-arrow,
            [data-export-chart-filters-prepared] .asper-chart-filter-select.ant-select .ant-select-clear {
                display: none !important;
            }
            [data-export-chart-filters-prepared] [data-chart-filter-bar],
            [data-export-chart-filters-prepared] [data-chart-filter-bar] .grid,
            [data-export-chart-filters-prepared] [data-chart-filter-bar] .flex.flex-col.items-stretch {
                overflow: visible !important;
            }
            [data-export-chart-filters-prepared] [data-chart-filter-bar] .grid {
                align-items: start !important;
            }
            [data-export-chart-filters-prepared] [data-chart-filter-bar] .flex.flex-col.items-stretch > div:first-child {
                overflow: visible !important;
                padding-top: 6px !important;
                min-height: 28px !important;
                line-height: 22px !important;
                white-space: nowrap !important;
            }
        `;
        clonedDocument.head.appendChild(style);
    }

    clonedDocument.querySelectorAll<HTMLElement>('[data-chart-filter-collapsed-row]').forEach((row) => {
        row.style.display = '';
    });

    clonedDocument.querySelectorAll<HTMLElement>('[data-chart-filter-bar]').forEach((bar) => {
        bar.style.paddingTop = '20px';
        bar.style.paddingBottom = '12px';
        bar.style.overflow = 'visible';
        bar.classList.remove('items-end');
        bar.classList.add('items-start');

        let ancestor: HTMLElement | null = bar.parentElement;
        for (let depth = 0; ancestor && depth < 4; depth += 1, ancestor = ancestor.parentElement) {
            ancestor.style.overflow = 'visible';
        }

        bar.querySelectorAll<HTMLElement>('.grid').forEach((grid) => {
            const visibleColumns = Array.from(grid.children).filter(
                (child): child is HTMLElement =>
                    child instanceof HTMLElement && child.style.display !== 'none',
            );
            const columnCount = visibleColumns.length;
            if (columnCount > 0) {
                grid.style.gridTemplateColumns = `repeat(${columnCount}, minmax(0, 1fr))`;
            }

            grid.classList.remove('items-end');
            grid.classList.add('items-start');
            grid.style.alignItems = 'start';
            grid.style.overflow = 'visible';

            visibleColumns.forEach((cell) => {
                cell.style.minWidth = '0';
                cell.style.width = '100%';
                cell.style.overflow = 'visible';
            });
        });

        bar.querySelectorAll<HTMLElement>('.flex.flex-col.items-stretch').forEach((column) => {
            column.style.overflow = 'visible';
            column.style.paddingBottom = '4px';
            column.style.minWidth = '0';
            column.style.width = '100%';

            const labelEl = column.querySelector<HTMLElement>(':scope > div:first-child');
            if (labelEl) {
                labelEl.style.marginTop = '0';
                labelEl.style.marginBottom = '4px';
                labelEl.style.paddingTop = '6px';
                labelEl.style.overflow = 'visible';
                labelEl.style.textOverflow = 'clip';
                labelEl.style.lineHeight = '22px';
                labelEl.style.minHeight = '28px';
                labelEl.style.whiteSpace = 'nowrap';
            }

            const controlEl = column.querySelector<HTMLElement>(':scope > div:last-child');
            if (controlEl && controlEl !== labelEl) {
                controlEl.style.marginTop = '0';
                controlEl.style.marginBottom = '0';
                controlEl.style.overflow = 'visible';
                controlEl.style.minWidth = '0';
                controlEl.style.width = '100%';
            }
        });
    });

    clonedDocument.querySelectorAll<HTMLElement>('.asper-chart-filter-select.ant-select').forEach((select) => {
        select.classList.remove('min-w-0');
        setExportStyleImportant(select, 'height', 'auto');
        setExportStyleImportant(select, 'min-height', '32px');
        setExportStyleImportant(select, 'overflow', 'visible');
        setExportStyleImportant(select, 'width', '100%');
        setExportStyleImportant(select, 'max-width', '100%');

        select.querySelectorAll<HTMLElement>('.ant-select-arrow, .ant-select-clear').forEach((el) => {
            el.style.display = 'none';
        });

        const selector = select.querySelector<HTMLElement>('.ant-select-selector');
        if (selector) {
            relaxChartFilterSelectTextForExport(selector);
            selector.style.paddingTop = '5px';
            selector.style.paddingBottom = '5px';
            selector.style.paddingRight = '11px';
            selector.style.display = 'flex';
            selector.style.alignItems = 'flex-start';
        }

        const selectionWrap = select.querySelector<HTMLElement>('.ant-select-selection-wrap');
        if (selectionWrap) {
            relaxChartFilterSelectTextForExport(selectionWrap);
            selectionWrap.style.alignItems = 'flex-start';
            selectionWrap.style.width = '100%';
        }

        select.querySelectorAll<HTMLElement>(
            [
                '.ant-select-selection-overflow',
                '.ant-select-selection-overflow-item',
                '.ant-select-selection-item',
                '.ant-select-selection-placeholder',
                '.ant-select-selection-search',
                '.ant-select-selection-search-input',
                '.asper-chart-filter-single-selected-value',
            ].join(', '),
        ).forEach((el) => {
            relaxChartFilterSelectTextForExport(el);
            if (el.tagName === 'INPUT') {
                setExportStyleImportant(el, 'height', '22px');
            }
        });

        select.querySelectorAll<HTMLElement>(
            '.ant-select-selection-item span, .ant-select-selection-placeholder, .asper-chart-filter-single-selected-value',
        ).forEach((el) => {
            relaxChartFilterSelectTextForExport(el);
            el.style.display = 'block';
        });

        const hasSelection = select.querySelector('.ant-select-selection-item');
        const placeholder = select.querySelector<HTMLElement>('.ant-select-selection-placeholder');
        if (hasSelection && placeholder) {
            placeholder.style.display = 'none';
        }
    });
}

/**
 * Force every element in the cloned DOM to use the same font stack as the
 * native PDF / DOCX / PPT text emissions. Without this, a captured chart
 * axis or table cell renders in Open Sans (the app's UI font), while the
 * native "Question" / heading text emitted directly by jsPDF/docx renders
 * in Helvetica — visibly different fonts on the same page.
 *
 * SVG `<text>` (charts use Recharts SVG) is normalised via the `font-family`
 * attribute so it isn't shadowed by Recharts’ inline attributes.
 */
function applyFontConsistencyToClone(clonedDocument: globalThis.Document): void {
    const html = clonedDocument.documentElement;
    if (html) html.style.fontFamily = EXPORT_FONT_FAMILY_CSS;
    const body = clonedDocument.body;
    if (body) body.style.fontFamily = EXPORT_FONT_FAMILY_CSS;

    const allElements = clonedDocument.querySelectorAll<HTMLElement>('*');
    allElements.forEach((el) => {
        const tag = el.tagName;
        if (tag === 'SCRIPT' || tag === 'STYLE') return;
        el.style.fontFamily = EXPORT_FONT_FAMILY_CSS;
    });

    const svgTexts = clonedDocument.querySelectorAll<SVGTextElement>('svg text, svg tspan');
    svgTexts.forEach((txt) => {
        txt.setAttribute('font-family', EXPORT_FONT_FAMILY_CSS);
    });
}

/**
 * Strip card chrome (border, shadow, oversized padding) that's redundant
 * when the captured image is placed onto a white PDF/DOCX page. This avoids
 * the “card-on-card” look and reclaims wasted whitespace around the content.
 *
 * Kept narrowly scoped — colored sections users expect (KeyTakeaways /
 * KeyFindings / Methodology boxes) keep their own chrome.
 */
function stripCardChromeForExport(clonedDocument: globalThis.Document): void {
    clonedDocument.querySelectorAll<HTMLElement>('[data-analysis-summary]').forEach((el) => {
        el.style.border = 'none';
        el.style.boxShadow = 'none';
        el.style.padding = '0';
        el.style.borderRadius = '0';
        el.style.background = 'transparent';
    });

    // `renderChartVisualizationContent` / `renderAnalysisSummaryContent` wrap
    // everything in `flex flex-col gap-4 px-4`. The horizontal `px-4` adds an
    // unwanted left/right gutter when this fallback wrapper is captured.
    clonedDocument.querySelectorAll<HTMLElement>('[data-export-content]').forEach((el) => {
        el.style.paddingLeft = '0';
        el.style.paddingRight = '0';
        el.querySelectorAll<HTMLElement>(':scope > div.px-4').forEach((inner) => {
            inner.style.paddingLeft = '0';
            inner.style.paddingRight = '0';
        });
    });

    // When the accordion outer card is part of the captured area (fallback
    // path), drop its border and vertical padding — the export already lays
    // the title out separately.
    clonedDocument.querySelectorAll<HTMLElement>('[data-ask-asper-accordion]').forEach((el) => {
        el.style.border = 'none';
        el.style.boxShadow = 'none';
        el.style.paddingTop = '0';
        el.style.paddingBottom = '0';
        el.style.borderRadius = '0';
        el.style.background = 'transparent';
    });
}

/**
 * Capture an HTMLElement as an HTMLCanvasElement using html2canvas.
 */
async function captureElementAsCanvas(element: HTMLElement, scale = CANVAS_SCALE): Promise<HTMLCanvasElement | null> {
    try {
        await new Promise((resolve) => setTimeout(resolve, 200));
        return await html2canvas(element, {
            backgroundColor: '#ffffff',
            scale,
            logging: false,
            useCORS: true,
            allowTaint: true,
            ignoreElements: isExportExcludedElement,
            onclone: (clonedDocument) => {
                relaxAntdTableHeadersForExport(clonedDocument);
                flattenAntdScrollTableForExport(clonedDocument);
                relaxChartLegendForExport(clonedDocument);
                applyFontConsistencyToClone(clonedDocument);
                prepareChartFiltersForExport(clonedDocument);
                syncAntdTableHeaderBodyForExport(clonedDocument);
                stripCardChromeForExport(clonedDocument);
            },
        });
    } catch (error) {
        console.error('Error capturing element as canvas:', error);
        return null;
    }
}

const isExportExcludedElement = (element: Element): boolean =>
    element instanceof HTMLElement && element.hasAttribute('data-export-exclude');

const isPptExcludedElement = (element: Element): boolean =>
    isExportExcludedElement(element) ||
    (element instanceof HTMLElement && element.hasAttribute('data-export-exclude-ppt'));

function injectPptSlideTitlesIntoClone(clonedDocument: globalThis.Document): void {
    const exportSections = clonedDocument.querySelectorAll<HTMLElement>(`[${PPT_SLIDE_TITLE_ATTR}]`);
    exportSections.forEach((section) => {
        const title = section.getAttribute(PPT_SLIDE_TITLE_ATTR)?.trim();
        if (!title) return;
        if (section.firstElementChild?.classList.contains(PPT_SLIDE_TITLE_CLASS)) return;

        const titleEl = clonedDocument.createElement('div');
        titleEl.className = PPT_SLIDE_TITLE_CLASS;
        titleEl.textContent = title;

        titleEl.style.fontFamily = EXPORT_FONT_FAMILY_CSS;
        titleEl.style.fontWeight = '700';
        titleEl.style.fontSize = '28px';
        titleEl.style.lineHeight = '1.2';
        titleEl.style.color = '#18181B';
        titleEl.style.margin = '0 0 28px 0';

        section.prepend(titleEl);

        const headings = section.querySelectorAll<HTMLElement>('h1, h2, h3, h4, h5, h6');
        headings.forEach((heading) => {
            heading.style.marginTop = '0';
            heading.style.marginBottom = '16px';
            heading.style.lineHeight = '1.3';
        });

        const paragraphs = section.querySelectorAll<HTMLElement>('p');
        paragraphs.forEach((paragraph) => {
            paragraph.style.marginTop = '0';
            paragraph.style.marginBottom = '14px';
            paragraph.style.lineHeight = '1.55';
        });

        const lists = section.querySelectorAll<HTMLElement>('ul, ol');
        lists.forEach((list) => {
            list.style.marginTop = '8px';
            list.style.marginBottom = '0';
            list.style.paddingLeft = '24px';
        });

        const listItems = section.querySelectorAll<HTMLElement>('li');
        listItems.forEach((item) => {
            item.style.marginBottom = '12px';
            item.style.lineHeight = '1.55';
        });
    });
}

async function captureElementAsCanvasForPPT(element: HTMLElement): Promise<HTMLCanvasElement | null> {
    try {
        await new Promise((resolve) => setTimeout(resolve, 200));
        return await html2canvas(element, {
            backgroundColor: '#ffffff',
            scale: CANVAS_SCALE,
            logging: false,
            useCORS: true,
            allowTaint: true,
            ignoreElements: isPptExcludedElement,
            onclone: (clonedDocument) => {
                relaxAntdTableHeadersForExport(clonedDocument);
                flattenAntdScrollTableForExport(clonedDocument);
                relaxChartLegendForExport(clonedDocument);
                applyFontConsistencyToClone(clonedDocument);
                prepareChartFiltersForExport(clonedDocument);
                syncAntdTableHeaderBodyForExport(clonedDocument);
                stripCardChromeForExport(clonedDocument);
                injectPptSlideTitlesIntoClone(clonedDocument);
            },
        });
    } catch (error) {
        console.error('Error capturing element as canvas for PPT:', error);
        return null;
    }
}

/**
 * Slice a tall canvas into page-height chunks and return each chunk as a base64 PNG
 * together with its pixel dimensions.
 */
function sliceCanvasIntoPages(
    sourceCanvas: HTMLCanvasElement,
    sliceHeightPx: number,
): { dataUrl: string; width: number; height: number }[] {
    const pages: { dataUrl: string; width: number; height: number }[] = [];
    const totalHeight = sourceCanvas.height;
    const width = sourceCanvas.width;
    let y = 0;

    while (y < totalHeight) {
        const h = Math.min(sliceHeightPx, totalHeight - y);
        const offscreen = document.createElement('canvas');
        offscreen.width = width;
        offscreen.height = h;
        const ctx = offscreen.getContext('2d');
        if (!ctx) break;
        ctx.drawImage(sourceCanvas, 0, y, width, h, 0, 0, width, h);
        pages.push({ dataUrl: offscreen.toDataURL('image/png'), width, height: h });
        y += h;
    }

    return pages;
}

/**
 * Collect child elements from a container for per-component export.
 * Looks for [data-export-slide] children first; falls back to direct children.
 */
function getExportableChildren(container: HTMLElement): HTMLElement[] {
    const marked = Array.from(container.querySelectorAll<HTMLElement>('[data-export-slide]'));
    if (marked.length > 0) return marked;
    return Array.from(container.children).filter(
        (child): child is HTMLElement => child instanceof HTMLElement,
    );
}

function getSlideCardsForContainer(container: HTMLElement): HTMLElement[] {
    const descendantSlideCards = Array.from(
        container.querySelectorAll<HTMLElement>('[data-export-slide]'),
    );
    const leafSlideCards = descendantSlideCards.filter(
        (card) => !card.querySelector<HTMLElement>('[data-export-slide]'),
    );

    if (leafSlideCards.length > 0) return leafSlideCards;
    if (descendantSlideCards.length > 0) return descendantSlideCards;
    if (container.matches('[data-export-slide]')) return [container];
    return [];
}

function buildElementsToProcessForExport(container: HTMLElement): HTMLElement[] {
    const slideCards = getSlideCardsForContainer(container);
    if (slideCards.length > 0) return slideCards;
    const marked = getExportableChildren(container);
    if (marked.length > 0) return marked;
    return [container];
}

/** Stable key so we emit one question heading per user task / orphan section. */
function getExportPartSourceKey(part: HTMLElement): string {
    const taskRoot = part.closest<HTMLElement>('[data-task-id]');
    const taskId = taskRoot?.getAttribute('data-task-id');
    if (taskId) return `task:${taskId}`;
    return `orphan:${part.getAttribute(PPT_SLIDE_TITLE_ATTR)?.trim() || 'default'}`;
}

/** Question text for a slide card: task accordion title, else slide title, else fallback. */
function getQuestionHeadingForExportPart(part: HTMLElement, fallbackTitle?: string): string | null {
    const taskRoot = part.closest<HTMLElement>('[data-task-id]');
    const fromTask = taskRoot?.getAttribute(PPT_SLIDE_TITLE_ATTR)?.trim();
    if (fromTask) return fromTask;
    const fromSlide = part.getAttribute(PPT_SLIDE_TITLE_ATTR)?.trim();
    if (fromSlide) return fromSlide;
    return fallbackTitle?.trim() || null;
}

/**
 * Native PDF heading for artefact section titles ("Summary",
 * "Top Product Groups", chart titles, etc.) and the matching
 * {@link appendPdfUserQueryPreamble} "Question" label — same size, weight,
 * and line height so they read as one consistent heading style. Keep this
 * in sync with {@link DOCX_HEADING_SIZE_HALF_PT}.
 */
const PDF_HEADING_FONT_SIZE_PT = 13;
const PDF_HEADING_LINE_HEIGHT_MM = 6;

function appendPdfWrappedTitle(
    pdf: jsPDF,
    title: string,
    maxWidth: number,
    pageHeight: number,
    yPosition: number,
): number {
    pdf.setFontSize(PDF_HEADING_FONT_SIZE_PT);
    pdf.setFont('helvetica', 'bold');
    const titleLines = pdf.splitTextToSize(title, maxWidth);
    titleLines.forEach((line: string) => {
        if (yPosition + PDF_HEADING_LINE_HEIGHT_MM > pageHeight - MARGIN) {
            pdf.addPage();
            yPosition = MARGIN;
        }
        pdf.text(line, MARGIN, yPosition);
        yPosition += PDF_HEADING_LINE_HEIGHT_MM;
    });
    return yPosition + SPACING;
}

/** Label + wrapped user query so exports show chat context before artefact sections. */
function appendPdfUserQueryPreamble(
    pdf: jsPDF,
    userQuery: string,
    maxWidth: number,
    pageHeight: number,
    yPosition: number,
): number {
    pdf.setFontSize(PDF_HEADING_FONT_SIZE_PT);
    pdf.setFont('helvetica', 'bold');
    if (yPosition + PDF_HEADING_LINE_HEIGHT_MM > pageHeight - MARGIN) {
        pdf.addPage();
        yPosition = MARGIN;
    }
    pdf.text('Question', MARGIN, yPosition);
    yPosition += PDF_HEADING_LINE_HEIGHT_MM;

    pdf.setFontSize(12);
    pdf.setFont('helvetica', 'normal');
    const lines = pdf.splitTextToSize(userQuery, maxWidth);
    lines.forEach((line: string) => {
        if (yPosition + 6 > pageHeight - MARGIN) {
            pdf.addPage();
            yPosition = MARGIN;
        }
        pdf.text(line, MARGIN, yPosition);
        yPosition += 6;
    });
    return yPosition + SPACING;
}

const EXPORT_TABLE_DEEPLINK_ATTR = 'data-export-table-deeplink-url';
const EXPORT_TABLE_PREVIEW_STATS_ATTR = 'data-export-table-preview-stats';
/**
 * Both table-level and run-level links share the same label; they're distinguished by font size
 * (table links render one point smaller than the per-question run links).
 */
const EXPORT_TABLE_DEEPLINK_LABEL = 'Open in AskAsper';
/** Per-question (runId) deep link appended after each question's response in exports. */
const EXPORT_RUN_DEEPLINK_ATTR = 'data-export-run-deeplink-url';
const EXPORT_RUN_DEEPLINK_LABEL = 'Open in AskAsper';
/** DOCX font size is half-points; table footer = 10pt, run link one point larger = 11pt. */
const EXPORT_TABLE_FOOTER_DOCX_SIZE_HALF_PT = 20;
const EXPORT_RUN_DEEPLINK_DOCX_SIZE_HALF_PT = 22;
/** PDF point sizes: table link one point smaller than the run link. */
const EXPORT_TABLE_DEEPLINK_PDF_FONT_PT = 10;
const EXPORT_RUN_DEEPLINK_PDF_FONT_PT = 11;
/** PPT point sizes: table link one point smaller than the run link. */
const EXPORT_RUN_DEEPLINK_PPT_FONT_SIZE = 11;

/** Read deep link URL from a captured export part (excluded from canvas; appended in PDF/DOCX). */
function readTableDeepLinkUrlFromExportPart(element: HTMLElement): string | undefined {
    const url = element
        .querySelector<HTMLElement>(`[${EXPORT_TABLE_DEEPLINK_ATTR}]`)
        ?.getAttribute(EXPORT_TABLE_DEEPLINK_ATTR)
        ?.trim();
    return url || undefined;
}

/** The right-panel question block ancestor for a captured export part (one block = one question/run). */
function getRightPanelBlockForPart(part: HTMLElement): HTMLElement | null {
    return part.closest<HTMLElement>(`[${RIGHT_PANEL_BLOCK_ATTR}]`);
}

/** Read the per-question (runId) deep link URL from a right-panel block element. */
function readRunDeepLinkUrlFromBlock(block: HTMLElement | null): string | undefined {
    if (!block) return undefined;
    const url = block
        .querySelector<HTMLElement>(`[${EXPORT_RUN_DEEPLINK_ATTR}]`)
        ?.getAttribute(EXPORT_RUN_DEEPLINK_ATTR)
        ?.trim();
    return url || undefined;
}

function readTablePreviewSummaryFromExportPart(element: HTMLElement): string | undefined {
    const raw = element
        .querySelector<HTMLElement>(`[${EXPORT_TABLE_PREVIEW_STATS_ATTR}]`)
        ?.getAttribute(EXPORT_TABLE_PREVIEW_STATS_ATTR)
        ?.trim();
    if (!raw) return undefined;
    try {
        const stats = JSON.parse(raw) as TablePreviewStats;
        const { shownRows, totalRows, shownColumns, totalColumns } = stats;
        if (
            typeof shownRows !== 'number' ||
            typeof totalRows !== 'number' ||
            typeof shownColumns !== 'number' ||
            typeof totalColumns !== 'number'
        ) {
            return undefined;
        }
        const resolved = resolveTablePreviewStatsForExport(element, stats);
        return formatTablePreviewSummary(resolved);
    } catch {
        return undefined;
    }
}

function appendPdfTextLine(
    pdf: jsPDF,
    text: string,
    yPosition: number,
    pageHeight: number,
    maxWidth: number,
    options?: { fontSize?: number; color?: [number, number, number] },
): number {
    const fontSize = options?.fontSize ?? 10;
    pdf.setFontSize(fontSize);
    pdf.setFont('helvetica', 'normal');
    if (options?.color) {
        pdf.setTextColor(...options.color);
    } else {
        pdf.setTextColor(102, 102, 102);
    }

    const lines = pdf.splitTextToSize(text, maxWidth);
    lines.forEach((line: string) => {
        if (yPosition + 5 > pageHeight - MARGIN) {
            pdf.addPage();
            yPosition = MARGIN;
        }
        pdf.text(line, MARGIN, yPosition);
        yPosition += 5;
    });

    pdf.setTextColor(0, 0, 0);
    return yPosition;
}

/** Append a clickable deep link below a captured table/chart block in the PDF. */
function appendPdfTableDeepLink(
    pdf: jsPDF,
    url: string,
    yPosition: number,
    pageHeight: number,
    maxWidth: number,
    label: string = EXPORT_TABLE_DEEPLINK_LABEL,
    fontSize: number = EXPORT_TABLE_DEEPLINK_PDF_FONT_PT,
): number {
    if (yPosition + 5 > pageHeight - MARGIN) {
        pdf.addPage();
        yPosition = MARGIN;
    }

    pdf.setFontSize(fontSize);
    pdf.setFont('helvetica', 'normal');
    pdf.setTextColor(95, 73, 188);
    pdf.textWithLink(label, MARGIN, yPosition, { url });
    yPosition += 5;

    pdf.setTextColor(0, 0, 0);
    return yPosition + SPACING;
}

/** Append the per-question (runId) deep link at the end of a question's response in the PDF. */
function appendPdfRunDeepLink(
    pdf: jsPDF,
    block: HTMLElement | null,
    yPosition: number,
    pageHeight: number,
    maxWidth: number,
): number {
    const url = readRunDeepLinkUrlFromBlock(block);
    if (!url) return yPosition;
    return appendPdfTableDeepLink(
        pdf,
        url,
        yPosition,
        pageHeight,
        maxWidth,
        EXPORT_RUN_DEEPLINK_LABEL,
        EXPORT_RUN_DEEPLINK_PDF_FONT_PT,
    );
}

const PPT_TABLE_FOOTER_FONT_SIZE = 10;
const PPT_TABLE_FOOTER_SUMMARY_COLOR = '666666';
const PPT_TABLE_FOOTER_LINK_COLOR = '5F49BC';
const PPT_TABLE_FOOTER_LINE_H = 0.22;
const PPT_TABLE_FOOTER_GAP = 0.08;

/** Descriptor shown top-right on task-level PPT slides when methodology is in speaker notes. */
export const PPT_METHODOLOGY_SPEAKER_NOTES_LABEL = 'Methodology in speaker notes';
const PPT_METHODOLOGY_BADGE_W = 2.25;
const PPT_METHODOLOGY_BADGE_H = 0.28;
const PPT_METHODOLOGY_BADGE_BG = 'F7F5FF';
const PPT_METHODOLOGY_BADGE_BORDER = 'E8E0FF';
const PPT_METHODOLOGY_BADGE_TEXT_COLOR = '5F49BC';
const PPT_METHODOLOGY_BADGE_FONT_SIZE = 9;

function getPptTableFooterBlockHeight(element: HTMLElement): number {
    const hasSummary = !!readTablePreviewSummaryFromExportPart(element);
    const hasLink = !!readTableDeepLinkUrlFromExportPart(element);
    if (!hasSummary && !hasLink) return 0;

    let height = 0;
    if (hasSummary) height += PPT_TABLE_FOOTER_LINE_H;
    if (hasLink) {
        if (hasSummary) height += PPT_TABLE_FOOTER_GAP;
        height += PPT_TABLE_FOOTER_LINE_H;
    }
    return height + PPT_TABLE_FOOTER_GAP;
}

/** Table preview line + Ask Asper hyperlink below exported table images in PPT. */
function addPptTableExportFooter(
    slide: PptxGenJS.Slide,
    element: HTMLElement,
    startX: number,
    startY: number,
    width: number,
): void {
    const previewSummary = readTablePreviewSummaryFromExportPart(element);
    const deepLinkUrl = readTableDeepLinkUrlFromExportPart(element);
    if (!previewSummary && !deepLinkUrl) return;

    let y = startY;

    if (previewSummary) {
        slide.addText(previewSummary, {
            x: startX,
            y,
            w: width,
            h: PPT_TABLE_FOOTER_LINE_H,
            fontFace: EXPORT_FONT_PRIMARY,
            fontSize: PPT_TABLE_FOOTER_FONT_SIZE,
            color: PPT_TABLE_FOOTER_SUMMARY_COLOR,
            align: 'left',
            valign: 'top',
            margin: 0,
        });
        y += PPT_TABLE_FOOTER_LINE_H + PPT_TABLE_FOOTER_GAP;
    }

    if (deepLinkUrl) {
        slide.addText(EXPORT_TABLE_DEEPLINK_LABEL, {
            x: startX,
            y,
            w: width,
            h: PPT_TABLE_FOOTER_LINE_H,
            fontFace: EXPORT_FONT_PRIMARY,
            fontSize: PPT_TABLE_FOOTER_FONT_SIZE,
            color: PPT_TABLE_FOOTER_LINK_COLOR,
            underline: { style: 'sng' },
            hyperlink: { url: deepLinkUrl },
            align: 'left',
            valign: 'top',
            margin: 0,
        });
    }
}

/** Table preview line + Ask Asper hyperlink below exported table images in Word. */
function buildDocxTableExportFooterParagraphs(element: HTMLElement): Paragraph[] {
    const paragraphs: Paragraph[] = [];

    const previewSummary = readTablePreviewSummaryFromExportPart(element);
    if (previewSummary) {
        paragraphs.push(
            new Paragraph({
                children: [
                    new TextRun({
                        text: previewSummary,
                        font: EXPORT_FONT_PRIMARY,
                        size: EXPORT_TABLE_FOOTER_DOCX_SIZE_HALF_PT,
                        color: '666666',
                    }),
                ],
                spacing: { before: 80, after: 60 },
            }),
        );
    }

    const deepLinkUrl = readTableDeepLinkUrlFromExportPart(element);
    if (deepLinkUrl) {
        paragraphs.push(
            new Paragraph({
                children: [
                    new ExternalHyperlink({
                        children: [
                            new TextRun({
                                text: EXPORT_TABLE_DEEPLINK_LABEL,
                                font: EXPORT_FONT_PRIMARY,
                                size: EXPORT_TABLE_FOOTER_DOCX_SIZE_HALF_PT,
                                color: '5F49BC',
                                underline: {},
                            }),
                        ],
                        link: deepLinkUrl,
                    }),
                ],
                spacing: { after: 120 },
            }),
        );
    }

    return paragraphs;
}

/** Per-question (runId) hyperlink paragraph appended after a question's response in Word. */
function buildDocxRunDeepLinkParagraphs(block: HTMLElement | null): Paragraph[] {
    const url = readRunDeepLinkUrlFromBlock(block);
    if (!url) return [];
    return [
        new Paragraph({
            children: [
                new ExternalHyperlink({
                    children: [
                        new TextRun({
                            text: EXPORT_RUN_DEEPLINK_LABEL,
                            font: EXPORT_FONT_PRIMARY,
                            size: EXPORT_RUN_DEEPLINK_DOCX_SIZE_HALF_PT,
                            color: '5F49BC',
                            underline: {},
                        }),
                    ],
                    link: url,
                }),
            ],
            spacing: { before: 80, after: 160 },
        }),
    ];
}

async function appendCapturedElementToPdf(
    pdf: jsPDF,
    element: HTMLElement,
    yPosition: number,
    pageHeight: number,
    maxWidth: number,
    usableHeight: number,
): Promise<number> {
    const canvas = await captureElementAsCanvas(element);
    if (!canvas) return yPosition;

    const pxPerMm = (canvas.width / CANVAS_SCALE) / maxWidth;
    const elementHeightMm = (canvas.height / CANVAS_SCALE) / pxPerMm;

    if (elementHeightMm <= usableHeight) {
        const remainingMm = pageHeight - MARGIN - yPosition;
        if (elementHeightMm > remainingMm) {
            pdf.addPage();
            yPosition = MARGIN;
        }
        pdf.addImage(canvas.toDataURL('image/png'), 'PNG', MARGIN, yPosition, maxWidth, elementHeightMm);
        yPosition += elementHeightMm + SPACING;
    } else {
        if (yPosition > MARGIN) {
            pdf.addPage();
            yPosition = MARGIN;
        }
        const totalCanvasHeight = canvas.height;
        const canvasWidth = canvas.width;
        let srcY = 0;
        while (srcY < totalCanvasHeight) {
            const availableMm = pageHeight - MARGIN - yPosition;
            const sliceHeightPx = Math.floor(availableMm * pxPerMm * CANVAS_SCALE);
            const actualSlice = Math.min(sliceHeightPx, totalCanvasHeight - srcY);

            const offscreen = document.createElement('canvas');
            offscreen.width = canvasWidth;
            offscreen.height = actualSlice;
            const ctx = offscreen.getContext('2d');
            if (!ctx) break;
            ctx.drawImage(canvas, 0, srcY, canvasWidth, actualSlice, 0, 0, canvasWidth, actualSlice);

            const sliceHeightMm = (actualSlice / CANVAS_SCALE) / pxPerMm;
            pdf.addImage(offscreen.toDataURL('image/png'), 'PNG', MARGIN, yPosition, maxWidth, sliceHeightMm);

            srcY += actualSlice;
            if (srcY < totalCanvasHeight) {
                pdf.addPage();
                yPosition = MARGIN;
            } else {
                yPosition += sliceHeightMm + SPACING;
            }
        }
    }

    const previewSummary = readTablePreviewSummaryFromExportPart(element);
    if (previewSummary) {
        yPosition = appendPdfTextLine(pdf, previewSummary, yPosition, pageHeight, maxWidth) + SPACING;
    }

    const deepLinkUrl = readTableDeepLinkUrlFromExportPart(element);
    if (deepLinkUrl) {
        yPosition = appendPdfTableDeepLink(pdf, deepLinkUrl, yPosition, pageHeight, maxWidth);
    }

    return yPosition;
}

/**
 * Export a container element to PDF.
 *
 * Instead of capturing the whole container as one giant image and blindly
 * slicing it (which cuts tables/cards in half), each direct child is captured
 * individually and placed whole on a page:
 *   - Fits in remaining space → placed immediately.
 *   - Doesn't fit but ≤ full page height → pushed to a fresh page.
 *   - Taller than a full page → sliced as a last resort.
 *
 * If the container has no usable children it falls back to capturing the
 * whole container as a single image.
 */
export async function exportContainerToPDF(
    container: HTMLElement | null,
    options?: ArtifactPdfDocxExportOptions,
): Promise<void> {
    if (!container) {
        console.error('Export container is null');
        return;
    }

    try {
        await new Promise((resolve) => setTimeout(resolve, 500));

        const pdf = new jsPDF('p', 'mm', 'a4');
        const pageWidth = pdf.internal.pageSize.getWidth();
        const pageHeight = pdf.internal.pageSize.getHeight();
        const maxWidth = pageWidth - 2 * MARGIN;
        const usableHeight = pageHeight - 2 * MARGIN;
        let yPosition = MARGIN;

        const elementsToProcess = buildElementsToProcessForExport(container);
        let lastSourceKey: string | null = null;
        let lastPrintedUserQuery: string | null = null;
        let lastRunDeepLinkBlock: HTMLElement | null = null;

        for (let partIndex = 0; partIndex < elementsToProcess.length; partIndex++) {
            const element = elementsToProcess[partIndex];
            const currentBlock = getRightPanelBlockForPart(element);
            // When the question block changes, close out the previous question with its
            // per-question (runId) deep link before starting the next one.
            if (currentBlock !== lastRunDeepLinkBlock && lastRunDeepLinkBlock !== null) {
                yPosition = appendPdfRunDeepLink(pdf, lastRunDeepLinkBlock, yPosition, pageHeight, maxWidth);
            }
            const uq = resolveUserQueryForExportPart(element, options);
            if (uq && uq !== lastPrintedUserQuery) {
                // Each new question starts on a fresh page so users can scan
                // the export one page = one question. Skip for the very first
                // question (we're already on page 1 at the top margin).
                if (lastPrintedUserQuery !== null) {
                    pdf.addPage();
                    yPosition = MARGIN;
                }
                yPosition = appendPdfUserQueryPreamble(pdf, uq, maxWidth, pageHeight, yPosition);
                lastPrintedUserQuery = uq;
            }
            lastRunDeepLinkBlock = currentBlock;

            const sourceKey = getExportPartSourceKey(element);
            if (sourceKey !== lastSourceKey) {
                const heading = getQuestionHeadingForExportPart(element, options?.title);
                if (heading) {
                    yPosition = appendPdfWrappedTitle(pdf, heading, maxWidth, pageHeight, yPosition);
                }
                lastSourceKey = sourceKey;
            }
            yPosition = await appendCapturedElementToPdf(
                pdf,
                element,
                yPosition,
                pageHeight,
                maxWidth,
                usableHeight,
            );
            if (isLastExportPartForScope(element, elementsToProcess, partIndex)) {
                yPosition = await appendMethodologyToPdf(
                    pdf,
                    getExportScopeForSlideCard(element),
                    yPosition,
                    pageHeight,
                    maxWidth,
                    usableHeight,
                );
            }
        }
        // Final question's per-question deep link.
        if (lastRunDeepLinkBlock !== null) {
            yPosition = appendPdfRunDeepLink(pdf, lastRunDeepLinkBlock, yPosition, pageHeight, maxWidth);
        }

        const filename = options?.filename || `${buildAskAsperExportBasename(options?.title || 'artifact')}.pdf`;

        pdf.save(filename);
    } catch (error) {
        console.error('Error exporting to PDF:', error);
        throw error;
    }
}

/**
 * Heading sizes (half-points). Explicitly set so Word doesn't fall back to
 * its default heading style (Calibri Light, larger sizes) — keeping body
 * and heading text in the same font family ({@link EXPORT_FONT_PRIMARY})
 * avoids the visible mismatch reported in PDF/Word exports.
 *
 * The "Question" label uses the same half-point size as {@link HeadingLevel.HEADING_2}
 * (typical first section title after a preamble, e.g. "Summary"). User query
 * body stays at 12pt. Mirror of {@link PDF_HEADING_FONT_SIZE_PT}.
 */
const DOCX_HEADING_SIZE_HALF_PT: Partial<Record<(typeof HeadingLevel)[keyof typeof HeadingLevel], number>> = {
    [HeadingLevel.HEADING_1]: 28, // 14pt
    [HeadingLevel.HEADING_2]: 26, // 13pt
    [HeadingLevel.HEADING_3]: 24, // 12pt
};

/**
 * Build a DOCX heading paragraph that uses {@link EXPORT_FONT_PRIMARY} for
 * both the run and the heading style override, so visually it matches the
 * surrounding body text.
 */
function buildDocxHeadingParagraph(
    text: string,
    level: (typeof HeadingLevel)[keyof typeof HeadingLevel],
): Paragraph {
    return new Paragraph({
        heading: level,
        spacing: { before: 160, after: 120 },
        children: [
            new TextRun({
                text,
                bold: true,
                font: EXPORT_FONT_PRIMARY,
                size: DOCX_HEADING_SIZE_HALF_PT[level] ?? 28,
                color: '18181B',
            }),
        ],
    });
}

/**
 * Forces a hard page break before the next paragraph in DOCX. Used to
 * separate questions onto their own pages so the export reads as one
 * page-per-question for tracking.
 */
function buildDocxPageBreakParagraph(): Paragraph {
    return new Paragraph({ children: [new PageBreak()] });
}

/**
 * "Question" preamble paragraphs (label + body). Spacing is intentionally
 * tight so the preamble doesn't push the first artefact off the top of the
 * page.
 */
function buildDocxQuestionPreambleParagraphs(userQuery: string): Paragraph[] {
    return [
        new Paragraph({
            children: [
                new TextRun({
                    text: 'Question',
                    bold: true,
                    font: EXPORT_FONT_PRIMARY,
                    size: DOCX_HEADING_SIZE_HALF_PT[HeadingLevel.HEADING_2] ?? 26, // 13pt — same as "Summary" (HEADING_2)
                    color: '18181B',
                }),
            ],
            spacing: { after: 60 },
        }),
        new Paragraph({
            children: [
                new TextRun({
                    text: userQuery,
                    font: EXPORT_FONT_PRIMARY,
                    size: 24, // 12pt
                    color: '18181B',
                }),
            ],
            spacing: { after: 180 },
        }),
    ];
}

/**
 * Convert a captured canvas into a centred image paragraph. Returns `null`
 * if the canvas can't be re-encoded as PNG. `spacing.after` kept tight (120
 * twips ≈ 6pt) so cards don't drift apart on the page.
 */
function buildDocxImageParagraph(
    canvas: HTMLCanvasElement,
    maxWidthPt: number,
    maxHeightPt: number,
): Paragraph | null {
    let width = maxWidthPt;
    let height = (canvas.height / canvas.width) * width;
    if (height > maxHeightPt) {
        height = maxHeightPt;
        width = (canvas.width / canvas.height) * height;
    }

    const base64Match = canvas.toDataURL('image/png').match(/^data:image\/[^;]+;base64,(.+)$/);
    if (!base64Match) return null;
    const imageBuffer = Uint8Array.from(atob(base64Match[1]), (c) => c.charCodeAt(0));

    return new Paragraph({
        children: [
            new ImageRun({
                data: imageBuffer,
                transformation: { width, height },
                type: 'png',
            }),
        ],
        spacing: { after: 120 },
        alignment: AlignmentType.CENTER,
    });
}

/**
 * Document-level defaults so that any text not wrapped in an explicit
 * `TextRun({ font })` (e.g. fallbacks, future additions) still renders in
 * the export font.
 */
function buildDocxDocumentOptions(children: Paragraph[]): ConstructorParameters<typeof DocxDocument>[0] {
    return {
        styles: {
            default: {
                document: {
                    run: { font: EXPORT_FONT_PRIMARY, size: 22 }, // 11pt
                },
            },
        },
        sections: [{ children }],
    };
}

/**
 * Export a container element to Word (DOCX).
 *
 * Each child element is captured individually and added as a single whole
 * image (no slicing). Word handles pagination natively, keeping each
 * component intact. Very tall elements are scaled down to fit within a
 * page's usable height.
 */
export async function exportContainerToDOCX(
    container: HTMLElement | null,
    options?: ArtifactPdfDocxExportOptions,
): Promise<void> {
    if (!container) {
        console.error('Export container is null');
        return;
    }

    const DOC_MAX_WIDTH_PT = 500;
    const DOC_MAX_HEIGHT_PT = 700;

    try {
        await new Promise((resolve) => setTimeout(resolve, 500));

        const docChildren: Paragraph[] = [];

        const elementsToProcess = buildElementsToProcessForExport(container);
        let lastSourceKey: string | null = null;
        let questionSectionIndex = 0;
        let lastPrintedUserQuery: string | null = null;
        let hasEmittedUserQuery = false;
        let lastRunDeepLinkBlock: HTMLElement | null = null;

        for (let partIndex = 0; partIndex < elementsToProcess.length; partIndex++) {
            const element = elementsToProcess[partIndex];
            const currentBlock = getRightPanelBlockForPart(element);
            if (currentBlock !== lastRunDeepLinkBlock && lastRunDeepLinkBlock !== null) {
                docChildren.push(...buildDocxRunDeepLinkParagraphs(lastRunDeepLinkBlock));
            }
            const uq = resolveUserQueryForExportPart(element, options);
            if (uq && uq !== lastPrintedUserQuery) {
                // New question → new page in Word (skip for the first one;
                // we're already at the top of the document).
                if (lastPrintedUserQuery !== null) {
                    docChildren.push(buildDocxPageBreakParagraph());
                }
                docChildren.push(...buildDocxQuestionPreambleParagraphs(uq));
                lastPrintedUserQuery = uq;
                hasEmittedUserQuery = true;
            }
            lastRunDeepLinkBlock = currentBlock;

            const sourceKey = getExportPartSourceKey(element);
            if (sourceKey !== lastSourceKey) {
                const heading = getQuestionHeadingForExportPart(element, options?.title);
                if (heading) {
                    const taskHeadingLevel =
                        !hasEmittedUserQuery && questionSectionIndex === 0
                            ? HeadingLevel.HEADING_1
                            : HeadingLevel.HEADING_2;
                    docChildren.push(buildDocxHeadingParagraph(heading, taskHeadingLevel));
                    questionSectionIndex += 1;
                }
                lastSourceKey = sourceKey;
            }

            const textParagraphs = addEditableDocxParagraphsFromCard(element);
            if (textParagraphs && textParagraphs.length > 0) {
                docChildren.push(...textParagraphs);
            } else {
                const canvas = await captureElementAsCanvas(element, 2);
                if (canvas) {
                    const imageParagraph = buildDocxImageParagraph(canvas, DOC_MAX_WIDTH_PT, DOC_MAX_HEIGHT_PT);
                    if (imageParagraph) docChildren.push(imageParagraph);
                }
                docChildren.push(...buildDocxTableExportFooterParagraphs(element));
            }

            if (isLastExportPartForScope(element, elementsToProcess, partIndex)) {
                const methodologyParagraphs = await buildMethodologyDocxParagraphs(
                    getExportScopeForSlideCard(element),
                    DOC_MAX_WIDTH_PT,
                    DOC_MAX_HEIGHT_PT,
                );
                docChildren.push(...methodologyParagraphs);
            }
        }
        if (lastRunDeepLinkBlock !== null) {
            docChildren.push(...buildDocxRunDeepLinkParagraphs(lastRunDeepLinkBlock));
        }

        if (docChildren.length === 0) {
            console.error('No content to export to DOCX');
            return;
        }

        const doc = new DocxDocument(buildDocxDocumentOptions(docChildren));

        const blob = await Packer.toBlob(doc);
        const filename = options?.filename || `${buildAskAsperExportBasename(options?.title || 'artifact')}.docx`;
        saveAs(blob, filename);
    } catch (error) {
        console.error('Error exporting to DOCX:', error);
        throw error;
    }
}

/**
 * Export multiple HTML elements to a single PDF.
 *
 * Strategy to avoid broken components:
 * - yPosition is tracked across all elements so they flow onto the same page.
 * - Before placing an element, its rendered height is measured.
 *   • Fits in remaining space  → place immediately (no page break).
 *   • Doesn't fit but ≤ full page height → start a fresh page, place whole.
 *   • Taller than a full page → unavoidable slice across pages.
 */
export async function exportMultipleElementsToPDF(
    elements: (HTMLElement | null)[],
    options?: ArtifactPdfDocxExportOptions,
): Promise<void> {
    const validElements = elements.filter((el): el is HTMLElement => el !== null);
    if (validElements.length === 0) {
        console.error('No valid elements to export to PDF');
        return;
    }

    try {
        await new Promise((resolve) => setTimeout(resolve, 500));

        const pdf = new jsPDF('p', 'mm', 'a4');
        const pageWidth = pdf.internal.pageSize.getWidth();
        const pageHeight = pdf.internal.pageSize.getHeight();
        const maxWidth = pageWidth - 2 * MARGIN;
        const usableHeight = pageHeight - 2 * MARGIN;
        let yPosition = MARGIN;

        let lastPrintedUserQueryMulti: string | null = null;
        let lastRunDeepLinkBlock: HTMLElement | null = null;
        for (const element of validElements) {
            const uq = resolveUserQueryForExportPart(element, options);
            if (uq && uq !== lastPrintedUserQueryMulti) {
                // New question → new page (skip for the first one).
                if (lastPrintedUserQueryMulti !== null) {
                    pdf.addPage();
                    yPosition = MARGIN;
                }
                yPosition = appendPdfUserQueryPreamble(pdf, uq, maxWidth, pageHeight, yPosition);
                lastPrintedUserQueryMulti = uq;
            }

            const heading = getQuestionHeadingForExportPart(element, options?.title);
            if (heading) {
                yPosition = appendPdfWrappedTitle(pdf, heading, maxWidth, pageHeight, yPosition);
            }

            const parts = buildElementsToProcessForExport(element);
            for (let partIndex = 0; partIndex < parts.length; partIndex++) {
                const part = parts[partIndex];
                const currentBlock = getRightPanelBlockForPart(part);
                if (currentBlock !== lastRunDeepLinkBlock && lastRunDeepLinkBlock !== null) {
                    yPosition = appendPdfRunDeepLink(pdf, lastRunDeepLinkBlock, yPosition, pageHeight, maxWidth);
                }
                lastRunDeepLinkBlock = currentBlock;
                yPosition = await appendCapturedElementToPdf(
                    pdf,
                    part,
                    yPosition,
                    pageHeight,
                    maxWidth,
                    usableHeight,
                );
                if (isLastExportPartForScope(part, parts, partIndex)) {
                    yPosition = await appendMethodologyToPdf(
                        pdf,
                        getExportScopeForSlideCard(part),
                        yPosition,
                        pageHeight,
                        maxWidth,
                        usableHeight,
                    );
                }
            }
        }
        if (lastRunDeepLinkBlock !== null) {
            yPosition = appendPdfRunDeepLink(pdf, lastRunDeepLinkBlock, yPosition, pageHeight, maxWidth);
        }

        const filename = options?.filename || `${buildAskAsperExportBasename(options?.title || 'artifact')}.pdf`;

        pdf.save(filename);
    } catch (error) {
        console.error('Error exporting multiple elements to PDF:', error);
        throw error;
    }
}

/**
 * Export multiple HTML elements to a single DOCX.
 *
 * Each element is added as one complete image — no slicing. Word handles
 * pagination natively, so components are never broken mid-content.
 * Very tall elements are scaled down to fit within a single page's usable
 * height (preserving aspect ratio) so content stays readable.
 */
export async function exportMultipleElementsToDOCX(
    elements: (HTMLElement | null)[],
    options?: ArtifactPdfDocxExportOptions,
): Promise<void> {
    const validElements = elements.filter((el): el is HTMLElement => el !== null);
    if (validElements.length === 0) {
        console.error('No valid elements to export to DOCX');
        return;
    }

    // A4 usable area in points (1 inch = 72 pt; A4 with ~1-inch margins)
    const DOC_MAX_WIDTH_PT = 500;
    const DOC_MAX_HEIGHT_PT = 700;

    try {
        await new Promise((resolve) => setTimeout(resolve, 500));

        const docChildren: Paragraph[] = [];

        let multiQuestionSectionIndex = 0;
        let lastPrintedUserQueryMulti: string | null = null;
        let multiHasEmittedUserQuery = false;
        let lastRunDeepLinkBlock: HTMLElement | null = null;
        for (let i = 0; i < validElements.length; i++) {
            const el = validElements[i];
            const uq = resolveUserQueryForExportPart(el, options);
            if (uq && uq !== lastPrintedUserQueryMulti) {
                // New question → new page in Word (skip for the first one).
                if (lastPrintedUserQueryMulti !== null) {
                    docChildren.push(buildDocxPageBreakParagraph());
                }
                docChildren.push(...buildDocxQuestionPreambleParagraphs(uq));
                lastPrintedUserQueryMulti = uq;
                multiHasEmittedUserQuery = true;
            }

            const heading = getQuestionHeadingForExportPart(el, options?.title);
            if (heading) {
                const taskHeadingLevel =
                    !multiHasEmittedUserQuery && multiQuestionSectionIndex === 0
                        ? HeadingLevel.HEADING_1
                        : HeadingLevel.HEADING_2;
                docChildren.push(buildDocxHeadingParagraph(heading, taskHeadingLevel));
                multiQuestionSectionIndex += 1;
            }

            const parts = buildElementsToProcessForExport(el);

            for (let partIndex = 0; partIndex < parts.length; partIndex++) {
                const part = parts[partIndex];
                const currentBlock = getRightPanelBlockForPart(part);
                if (currentBlock !== lastRunDeepLinkBlock && lastRunDeepLinkBlock !== null) {
                    docChildren.push(...buildDocxRunDeepLinkParagraphs(lastRunDeepLinkBlock));
                }
                lastRunDeepLinkBlock = currentBlock;
                const textParagraphs = addEditableDocxParagraphsFromCard(part);
                if (textParagraphs && textParagraphs.length > 0) {
                    docChildren.push(...textParagraphs);
                } else {
                    const canvas = await captureElementAsCanvas(part, 2);
                    if (canvas) {
                        const imageParagraph = buildDocxImageParagraph(canvas, DOC_MAX_WIDTH_PT, DOC_MAX_HEIGHT_PT);
                        if (imageParagraph) docChildren.push(imageParagraph);
                    }
                    docChildren.push(...buildDocxTableExportFooterParagraphs(part));
                }

                if (isLastExportPartForScope(part, parts, partIndex)) {
                    const methodologyParagraphs = await buildMethodologyDocxParagraphs(
                        getExportScopeForSlideCard(part),
                        DOC_MAX_WIDTH_PT,
                        DOC_MAX_HEIGHT_PT,
                    );
                    docChildren.push(...methodologyParagraphs);
                }
            }
        }
        if (lastRunDeepLinkBlock !== null) {
            docChildren.push(...buildDocxRunDeepLinkParagraphs(lastRunDeepLinkBlock));
        }

        if (docChildren.length === 0) {
            console.error('No content to export to DOCX');
            return;
        }

        const doc = new DocxDocument(buildDocxDocumentOptions(docChildren));

        const blob = await Packer.toBlob(doc);
        const filename = options?.filename || `${buildAskAsperExportBasename(options?.title || 'artifact')}.docx`;
        saveAs(blob, filename);
    } catch (error) {
        console.error('Error exporting multiple elements to DOCX:', error);
        throw error;
    }
}

// Inline Asper logo SVG (src/assets/AsperLogo.svg)
const ASPER_LOGO_SVG = `<svg xmlns="http://www.w3.org/2000/svg" width="94" height="32" viewBox="0 0 94 32" fill="none">
  <path d="M15.967 28.5026L19.8351 20.6577C20.1634 19.9897 20.7225 19.4651 21.4087 19.1765L30.9724 15.1682C31.5514 14.9258 32.1205 15.5311 31.8434 16.0936L27.9753 23.9385C27.6471 24.6066 27.0879 25.1311 26.4017 25.4198L16.838 29.428C16.259 29.6705 15.6899 29.0651 15.967 28.5026Z" fill="#42A531"/>
  <path d="M3.43259 16.0078L11.2775 19.8759C11.9456 20.2041 12.4701 20.7633 12.7588 21.4495L16.767 31.0132C17.0095 31.5922 16.4041 32.1612 15.8417 31.8841L7.99672 28.0161C7.32867 27.6878 6.80414 27.1287 6.51548 26.4425L2.50723 16.8788C2.26476 16.2998 2.87012 15.7307 3.43259 16.0078Z" fill="#CA3D3D"/>
  <path d="M15.9917 2.96265C15.9785 2.92141 15.962 2.88017 15.9406 2.84223C15.7872 2.55687 15.4342 2.38368 15.0779 2.53378L5.51416 6.54203C4.82798 6.82904 4.2688 7.35522 3.94056 8.02327L2.27953 11.3932L0.0725133 15.8666C-0.2046 16.429 0.364473 17.0344 0.943442 16.7919L10.5072 12.7837C11.1933 12.495 11.7525 11.9705 12.0824 11.3024L13.6989 8.02327L15.9505 3.45749C16.0346 3.28924 16.0412 3.1144 15.9933 2.96265H15.9917Z" fill="#E8A122"/>
  <path d="M29.4053 15.0776L25.3971 5.51227C25.1678 4.96959 24.7901 4.50774 24.3117 4.17619C24.1864 4.08877 24.0544 4.00794 23.9158 3.93866L16.0709 0.0722692C15.5084 -0.204844 14.9031 0.364228 15.1455 0.943198L19.1538 10.5069C19.4408 11.1931 19.967 11.7523 20.635 12.0805L28.48 15.9486C28.762 16.0871 29.054 16.0145 29.2453 15.8331C29.4367 15.6533 29.5274 15.3663 29.4053 15.0776Z" fill="#293BB8"/>
  <path d="M38.0016 15.469C38.0016 14.3969 38.221 13.4467 38.6614 12.6187C39.1134 11.7923 39.7221 11.1523 40.4841 10.6987C41.2594 10.2336 42.1122 10.001 43.0425 10.001C43.8821 10.001 44.6128 10.1692 45.233 10.5057C45.7839 10.7861 46.2375 11.1309 46.5922 11.5399C46.6433 11.5993 46.7439 11.563 46.7439 11.4855V10.2583C46.7439 10.2138 46.7802 10.1775 46.8247 10.1775H48.8915C48.9361 10.1775 48.9724 10.2138 48.9724 10.2583V20.777C48.9724 20.8216 48.9361 20.8579 48.8915 20.8579H46.8247C46.7802 20.8579 46.7439 20.8216 46.7439 20.777V19.5152C46.7439 19.436 46.6416 19.4014 46.5905 19.4608C46.2342 19.8797 45.774 20.236 45.2132 20.528C44.5666 20.8645 43.8309 21.0327 43.0029 21.0327C42.0858 21.0327 41.2445 20.8001 40.4825 20.335C39.7204 19.8566 39.1118 19.1968 38.6598 18.3573C38.221 17.5045 38 16.5412 38 15.469H38.0016ZM46.7456 15.5086C46.7456 14.7713 46.5905 14.1329 46.2804 13.5886C45.9835 13.0459 45.5893 12.6319 45.0977 12.3482C44.6062 12.0645 44.0767 11.921 43.5076 11.921C42.9386 11.921 42.4091 12.0628 41.9175 12.3482C41.426 12.6204 41.0252 13.0261 40.715 13.5688C40.4181 14.0983 40.2697 14.7317 40.2697 15.469C40.2697 16.2063 40.4181 16.8513 40.715 17.4072C41.0252 17.963 41.426 18.3903 41.9175 18.6871C42.4223 18.9709 42.9518 19.1144 43.5076 19.1144C44.0635 19.1144 44.6062 18.9725 45.0977 18.6871C45.5893 18.4035 45.9835 17.9894 46.2804 17.4468C46.5905 16.8909 46.7456 16.2443 46.7456 15.5086Z" fill="#271F49"/>
  <path d="M54.8396 21.0334C54 21.0334 53.2428 20.8849 52.5715 20.588C51.9117 20.2779 51.3888 19.8639 51.0012 19.3476C50.6301 18.8412 50.4255 18.2804 50.3859 17.6685C50.3826 17.6223 50.4206 17.5843 50.4668 17.5843H52.5946C52.6375 17.5843 52.6721 17.6173 52.6771 17.6586C52.7315 18.0792 52.9426 18.4355 53.3088 18.7274C53.7097 19.0243 54.2078 19.1727 54.8016 19.1727C55.3954 19.1727 55.9002 19.0556 56.2367 18.823C56.5864 18.5773 56.7595 18.2672 56.7595 17.8928C56.7595 17.492 56.5649 17.1951 56.1773 17.0004C55.8028 16.8058 55.2008 16.593 54.3744 16.3604C53.5727 16.141 52.9195 15.9282 52.4165 15.7204C51.9117 15.5142 51.473 15.1975 51.0985 14.7703C50.7373 14.3431 50.5558 13.7823 50.5558 13.0829C50.5558 12.5138 50.7241 11.9975 51.0606 11.5324C51.3971 11.054 51.8754 10.6796 52.4956 10.4074C53.129 10.1353 53.8532 10 54.6664 10C55.882 10 56.8569 10.3101 57.5942 10.9303C58.3233 11.5208 58.7224 12.3258 58.7917 13.3452C58.795 13.3913 58.7571 13.4309 58.7109 13.4309H56.6622C56.6193 13.4309 56.5847 13.3979 56.5798 13.3567C56.527 12.913 56.3356 12.5567 56.0057 12.2878C55.656 12.0041 55.1843 11.8606 54.5905 11.8606C53.9967 11.8606 53.5629 11.9711 53.2527 12.1905C52.9426 12.4099 52.7876 12.7019 52.7876 13.0631C52.7876 13.3468 52.8915 13.586 53.0977 13.7806C53.3039 13.9753 53.5562 14.1303 53.8532 14.2458C54.1501 14.3497 54.5905 14.4849 55.1711 14.6532C55.9464 14.8594 56.5798 15.0738 57.0713 15.2932C57.576 15.4994 58.0082 15.8095 58.3694 16.2235C58.7307 16.6375 58.9187 17.1868 58.9319 17.8713C58.9319 18.4784 58.7637 19.021 58.4272 19.4993C58.0907 19.9777 57.6123 20.3521 56.9921 20.6243C56.3851 20.8964 55.6676 21.0317 54.8396 21.0317V21.0334Z" fill="#271F49"/>
  <path d="M62.6103 11.5053C62.6103 11.5828 62.7109 11.6191 62.7637 11.5597C63.12 11.149 63.5802 10.7993 64.1426 10.5057C64.7892 10.1692 65.5183 10.001 66.3332 10.001C67.2635 10.001 68.1096 10.2336 68.8734 10.6987C69.6486 11.1507 70.2556 11.7907 70.696 12.6187C71.1348 13.4451 71.3558 14.3952 71.3558 15.469C71.3558 16.5428 71.1365 17.5045 70.696 18.3573C70.2573 19.1968 69.6486 19.8566 68.8734 20.335C68.1113 20.8001 67.2635 21.0327 66.3332 21.0327C65.5183 21.0327 64.7958 20.8711 64.1624 20.5478C63.6049 20.2525 63.1381 19.8962 62.7604 19.4805C62.7092 19.4245 62.6103 19.4591 62.6103 19.5366V25.8558C62.6103 25.9003 62.574 25.9366 62.5294 25.9366H60.4824C60.4379 25.9366 60.4016 25.9003 60.4016 25.8558V10.2583C60.4016 10.2138 60.4379 10.1775 60.4824 10.1775H62.5294C62.574 10.1775 62.6103 10.2138 62.6103 10.2583V11.5053ZM69.1043 15.469C69.1043 14.7317 68.9492 14.0983 68.6391 13.5688C68.3422 13.0261 67.9414 12.6187 67.4367 12.3482C66.9451 12.0645 66.4156 11.921 65.8466 11.921C65.2775 11.921 64.7612 12.0628 64.2565 12.3482C63.7649 12.6319 63.3641 13.0459 63.054 13.5886C62.7571 14.1313 62.6086 14.7713 62.6086 15.5086C62.6086 16.2459 62.7571 16.8909 63.054 17.4468C63.3641 17.9894 63.7649 18.4035 64.2565 18.6871C64.7612 18.9709 65.2907 19.1144 65.8466 19.1144C66.4024 19.1144 66.9451 18.9725 67.4367 18.6871C67.9414 18.3903 68.3406 17.963 68.6391 17.4072C68.9492 16.8513 69.1043 16.2047 69.1043 15.469Z" fill="#271F49"/>
  <path d="M83.026 15.2562C83.026 15.629 83.0029 15.9671 82.9583 16.2739C82.9517 16.3135 82.9188 16.3432 82.8775 16.3432H74.8726C74.8247 16.3432 74.7868 16.3844 74.7917 16.4306C74.8709 17.2438 75.1843 17.8986 75.7336 18.3984C76.3027 18.9147 77.0004 19.1736 77.8268 19.1736C78.9979 19.1736 79.8309 18.692 80.3241 17.7271C80.339 17.6991 80.367 17.6809 80.3983 17.6809H82.6202C82.6763 17.6809 82.7142 17.7353 82.6961 17.7881C82.3662 18.7217 81.7905 19.4953 80.9691 20.104C80.1295 20.7242 79.0821 21.0343 77.8284 21.0343C76.8074 21.0343 75.8903 20.8083 75.0755 20.3563C74.2738 19.8912 73.6404 19.2446 73.1753 18.4182C72.7233 17.5786 72.4973 16.6087 72.4973 15.5102C72.4973 14.4116 72.7167 13.4483 73.1571 12.6219C73.6091 11.7824 74.2359 11.1358 75.0375 10.6838C75.8524 10.2318 76.7827 10.0059 77.8301 10.0059C78.8775 10.0059 79.7369 10.2252 80.5254 10.6657C81.3138 11.1044 81.9274 11.7263 82.3662 12.5263C82.8049 13.3147 83.026 14.2252 83.026 15.2595V15.2562ZM80.6359 14.5584C80.6804 14.5584 80.7183 14.5205 80.7167 14.476C80.6854 13.7007 80.3967 13.0755 79.8474 12.6005C79.2783 12.109 78.574 11.8632 77.7344 11.8632C76.9724 11.8632 76.3192 12.109 75.7765 12.6005C75.2536 13.0607 74.9353 13.6826 74.8198 14.4677C74.8132 14.5155 74.8511 14.5601 74.899 14.5601H80.6375L80.6359 14.5584Z" fill="#271F49"/>
  <path d="M86.4948 11.4314C86.4948 11.5139 86.6086 11.5468 86.6564 11.4792C86.9566 11.0553 87.3294 10.7188 87.7748 10.4681C88.2977 10.1695 88.9096 10.0145 89.6123 10.0029C89.6569 10.0029 89.6931 10.0392 89.6931 10.0838V12.2083C89.6931 12.2528 89.6569 12.2891 89.6123 12.2891H89.1323C88.2663 12.2891 87.6082 12.5085 87.1546 12.9489C86.7158 13.3877 86.4948 14.1514 86.4948 15.2367V20.7773C86.4948 20.8218 86.4585 20.8581 86.414 20.8581H84.367C84.3224 20.8581 84.2861 20.8218 84.2861 20.7773V10.2586C84.2861 10.2141 84.3224 10.1778 84.367 10.1778H86.414C86.4585 10.1778 86.4948 10.2141 86.4948 10.2586V11.4314Z" fill="#271F49"/>
</svg>`;

/**
 * Convert an SVG string to a PNG data URL using an off-screen canvas.
 */
async function svgToPngDataUrl(svgString: string, naturalWidth: number, naturalHeight: number): Promise<string> {
    return new Promise((resolve, reject) => {
        const scale = 2;
        const blob = new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' });
        const url = URL.createObjectURL(blob);
        const img = new Image();
        img.onload = () => {
            const offscreen = document.createElement('canvas');
            offscreen.width = naturalWidth * scale;
            offscreen.height = naturalHeight * scale;
            const ctx = offscreen.getContext('2d');
            if (!ctx) {
                URL.revokeObjectURL(url);
                reject(new Error('Failed to get 2d context'));
                return;
            }
            ctx.scale(scale, scale);
            ctx.drawImage(img, 0, 0, naturalWidth, naturalHeight);
            URL.revokeObjectURL(url);
            resolve(offscreen.toDataURL('image/png'));
        };
        img.onerror = () => {
            URL.revokeObjectURL(url);
            reject(new Error('Failed to load SVG image for logo conversion'));
        };
        img.src = url;
    });
}

const SLIDE_W = 13.33; // inches — LAYOUT_WIDE (16:9)
const SLIDE_H = 7.5;
const SLIDE_MARGIN = 0.3;
const LOGO_W = 1.1; // inches
const LOGO_H = (32 / 94) * LOGO_W; // maintain 94:32 aspect ratio
const LOGO_X = SLIDE_W - SLIDE_MARGIN - LOGO_W;
const LOGO_Y = SLIDE_H - SLIDE_MARGIN - LOGO_H;

// Usable content area (leaves room for logo strip at bottom)
const CONTENT_X = SLIDE_MARGIN;
const CONTENT_Y = SLIDE_MARGIN;
const CONTENT_W = SLIDE_W - 2 * SLIDE_MARGIN;
const CONTENT_H = LOGO_Y - SLIDE_MARGIN * 1.5;

type PptCoverSlideOptions = {
    threadTitle: string;
    downloadedAt?: Date;
};

type PptExportOptions = {
    filename?: string;
    title?: string;
    coverSlide?: PptCoverSlideOptions;
};

function createCoverGradientDataUrl(width = 1920, height = 1080): string {
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (!ctx) return '';

    // Base vertical gradient (top darker, bottom lighter) to match welcome-style cover.
    const vertical = ctx.createLinearGradient(0, 0, 0, height);
    vertical.addColorStop(0, '#4933B5');
    vertical.addColorStop(1, '#7A61D8');
    ctx.fillStyle = vertical;
    ctx.fillRect(0, 0, width, height);

    // Soft center glow for depth.
    const glow = ctx.createRadialGradient(
        width * 0.6,
        height * 0.45,
        width * 0.05,
        width * 0.6,
        height * 0.45,
        width * 0.75,
    );
    glow.addColorStop(0, 'rgba(255,255,255,0.18)');
    glow.addColorStop(1, 'rgba(255,255,255,0)');
    ctx.fillStyle = glow;
    ctx.fillRect(0, 0, width, height);

    return canvas.toDataURL('image/png');
}

function formatCoverSlideDate(downloadedAt: Date): string {
    try {
        return new Intl.DateTimeFormat(undefined, {
            dateStyle: 'medium',
        }).format(downloadedAt);
    } catch {
        return downloadedAt.toLocaleDateString();
    }
}

function addPptLogo(slide: PptxGenJS.Slide, logoDataUrl: string): void {
    slide.addImage({
        data: logoDataUrl,
        x: LOGO_X,
        y: LOGO_Y,
        w: LOGO_W,
        h: LOGO_H,
    });
}

/** Encode methodology props for {@link PPT_METHODOLOGY_ATTR} on the export DOM. */
export function serializeMethodologyForPptExport(payload: PptMethodologyExportPayload): string {
    return encodeURIComponent(JSON.stringify(payload));
}

function getMethodologyCaptureElementForScope(scope: HTMLElement): HTMLElement | null {
    return scope.querySelector<HTMLElement>(`[${METHODOLOGY_CAPTURE_ATTR}]`);
}

function isLastExportPartForScope(part: HTMLElement, allParts: HTMLElement[], partIndex: number): boolean {
    const scope = getExportScopeForSlideCard(part);
    for (let i = partIndex + 1; i < allParts.length; i++) {
        if (getExportScopeForSlideCard(allParts[i]) === scope) return false;
    }
    return true;
}

async function appendMethodologyToPdf(
    pdf: jsPDF,
    scope: HTMLElement,
    yPosition: number,
    pageHeight: number,
    maxWidth: number,
    usableHeight: number,
): Promise<number> {
    const captureEl = getMethodologyCaptureElementForScope(scope);
    if (!captureEl) return yPosition;
    return appendCapturedElementToPdf(pdf, captureEl, yPosition, pageHeight, maxWidth, usableHeight);
}

async function buildMethodologyDocxParagraphs(
    scope: HTMLElement,
    maxWidthPt: number,
    maxHeightPt: number,
): Promise<Paragraph[]> {
    const captureEl = getMethodologyCaptureElementForScope(scope);
    if (!captureEl) return [];

    const canvas = await captureElementAsCanvas(captureEl, 2);
    if (!canvas) return [];

    const imageParagraph = buildDocxImageParagraph(canvas, maxWidthPt, maxHeightPt);
    return imageParagraph ? [imageParagraph] : [];
}

/** Format methodology sections/formulae as plain text for PPT speaker notes. */
export function formatMethodologyForPptSpeakerNotes(payload: PptMethodologyExportPayload): string {
    const sections = payload.sections ?? [];
    const formulas = payload.formula
        ? Array.isArray(payload.formula)
            ? payload.formula.filter(Boolean)
            : [payload.formula]
        : [];
    if (sections.length === 0 && formulas.length === 0) return '';

    const lines: string[] = [payload.title?.trim() || 'Methodology'];
    for (const section of sections) {
        const sectionTitle = section.title?.trim();
        if (sectionTitle) {
            lines.push('');
            lines.push(sectionTitle);
        }
        for (const item of section.items ?? []) {
            const text = item?.trim();
            if (text) lines.push(`• ${text}`);
        }
    }
    if (formulas.length > 0) {
        lines.push('');
        for (const formula of formulas) {
            const text = formula.trim();
            if (text) lines.push(text);
        }
    }
    return lines.join('\n');
}

function parseMethodologyExportPayload(raw: string | null): PptMethodologyExportPayload | null {
    if (!raw?.trim()) return null;
    try {
        const decoded = decodeURIComponent(raw);
        const parsed = JSON.parse(decoded) as PptMethodologyExportPayload;
        if (!parsed || typeof parsed !== 'object') return null;
        return parsed;
    } catch {
        return null;
    }
}

function getExportScopeForSlideCard(card: HTMLElement): HTMLElement {
    const exportContent = card.closest<HTMLElement>('[data-export-content]');
    if (exportContent) return exportContent;
    const taskRoot = card.closest<HTMLElement>('[data-task-id]');
    if (taskRoot) return taskRoot;
    return card;
}

function getMethodologySpeakerNotesForScope(scope: HTMLElement): string | undefined {
    const methodologyEl = scope.querySelector<HTMLElement>(`[${PPT_METHODOLOGY_ATTR}]`);
    if (!methodologyEl) return undefined;
    const payload = parseMethodologyExportPayload(methodologyEl.getAttribute(PPT_METHODOLOGY_ATTR));
    if (!payload) return undefined;
    const notes = formatMethodologyForPptSpeakerNotes(payload);
    return notes.trim() ? notes : undefined;
}

type PptSlideWithNotes = PptxGenJS.Slide & { addNotes: (text: string) => void };

function isTaskLevelExportScope(scope: HTMLElement): boolean {
    return scope.hasAttribute('data-task-id') || !!scope.closest('[data-task-id]');
}

/** Small pill badge in the top-right corner of task-level slides. */
function addPptMethodologySpeakerNotesBadge(slide: PptxGenJS.Slide): void {
    const badgeX = SLIDE_W - SLIDE_MARGIN - PPT_METHODOLOGY_BADGE_W;
    slide.addText(PPT_METHODOLOGY_SPEAKER_NOTES_LABEL, {
        x: badgeX,
        y: CONTENT_Y,
        w: PPT_METHODOLOGY_BADGE_W,
        h: PPT_METHODOLOGY_BADGE_H,
        fontFace: EXPORT_FONT_PRIMARY,
        fontSize: PPT_METHODOLOGY_BADGE_FONT_SIZE,
        color: PPT_METHODOLOGY_BADGE_TEXT_COLOR,
        align: 'center',
        valign: 'middle',
        margin: 0,
        fill: { color: PPT_METHODOLOGY_BADGE_BG },
        line: { color: PPT_METHODOLOGY_BADGE_BORDER, width: 0.5 },
        rectRadius: 0.08,
    });
}

function addPptSpeakerNotesFromScope(slide: PptxGenJS.Slide, scope: HTMLElement): void {
    const notes = getMethodologySpeakerNotesForScope(scope);
    if (!notes) return;
    (slide as PptSlideWithNotes).addNotes(notes);
    if (isTaskLevelExportScope(scope)) {
        addPptMethodologySpeakerNotesBadge(slide);
    }
}

function addPptCoverSlide(
    pptx: PptxGenJS,
    logoDataUrl: string,
    coverSlide: PptCoverSlideOptions,
): void {
    const slide = pptx.addSlide();
    const gradientDataUrl = createCoverGradientDataUrl();
    if (gradientDataUrl) {
        slide.addImage({
            data: gradientDataUrl,
            x: 0,
            y: 0,
            w: SLIDE_W,
            h: SLIDE_H,
        });
    } else {
        slide.background = { color: '5B50D1' };
    }

    const titleText = coverSlide.threadTitle.trim() || 'Untitled Chat';
    const downloadedAt = coverSlide.downloadedAt ?? new Date();
    const dateText = formatCoverSlideDate(downloadedAt);

    slide.addText(titleText, {
        x: SLIDE_MARGIN,
        y: 2.55,
        w: CONTENT_W,
        h: 1.2,
        fontFace: EXPORT_FONT_PRIMARY,
        fontSize: 34,
        bold: true,
        color: 'F8F7FF',
        align: 'center',
        valign: 'middle',
    });
    slide.addText(dateText, {
        x: SLIDE_MARGIN,
        y: 3.95,
        w: CONTENT_W,
        h: 0.8,
        fontFace: EXPORT_FONT_PRIMARY,
        fontSize: 22,
        color: 'E8E2FF',
        align: 'center',
        valign: 'middle',
    });

    addPptLogo(slide, logoDataUrl);
}

const PPT_CSS_PX_PER_INCH = 96;

type PptCanvasPlacement = {
    imgX: number;
    imgY: number;
    imgW: number;
    imgH: number;
};

/**
 * Compute slide image placement at natural capture size (96 CSS px = 1 inch).
 * Scales down only when the capture exceeds the available area — never scales up.
 */
function computePptImagePlacement(
    canvas: HTMLCanvasElement,
    maxContentH: number,
): PptCanvasPlacement {
    const cssWidthPx = canvas.width / CANVAS_SCALE;
    const cssHeightPx = canvas.height / CANVAS_SCALE;
    const aspectRatio = cssHeightPx / cssWidthPx;

    let imgW = cssWidthPx / PPT_CSS_PX_PER_INCH;
    let imgH = imgW * aspectRatio;

    if (imgW > CONTENT_W) {
        imgW = CONTENT_W;
        imgH = imgW * aspectRatio;
    }
    if (imgH > maxContentH) {
        imgH = maxContentH;
        imgW = imgH / aspectRatio;
    }

    return {
        imgX: CONTENT_X + (CONTENT_W - imgW) / 2,
        imgY: CONTENT_Y + (maxContentH - imgH) / 2,
        imgW,
        imgH,
    };
}

/**
 * Add a captured canvas image to a pptxgenjs slide at natural size (centered).
 * Returns placement so callers can attach a table footer directly beneath the image.
 */
function addCanvasToSlide(
    slide: PptxGenJS.Slide,
    canvas: HTMLCanvasElement,
    maxContentH: number = CONTENT_H,
): PptCanvasPlacement {
    const { imgX, imgY, imgW, imgH } = computePptImagePlacement(canvas, maxContentH);

    slide.addImage({
        data: canvas.toDataURL('image/png'),
        x: imgX,
        y: imgY,
        w: imgW,
        h: imgH,
    });

    return { imgX, imgY, imgW, imgH };
}

type EditablePptRun = {
    text: string;
    color?: string;
    bold?: boolean;
};

type EditablePptLine = {
    runs: EditablePptRun[];
    bullet?: boolean;
    heading?: boolean;
};

function normalizeText(text: string): string {
    return text.replace(/\s+/g, ' ').trim();
}

function colorToPptHex(colorValue: string): string | undefined {
    const value = colorValue.trim().toLowerCase();
    if (!value) return undefined;

    const hexMatch = value.match(/^#([0-9a-f]{3}|[0-9a-f]{6})$/i);
    if (hexMatch) {
        const hex = hexMatch[1];
        if (hex.length === 3) {
            return `${hex[0]}${hex[0]}${hex[1]}${hex[1]}${hex[2]}${hex[2]}`.toUpperCase();
        }
        return hex.toUpperCase();
    }

    const rgbMatch = value.match(/^rgba?\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})/i);
    if (rgbMatch) {
        const [r, g, b] = [rgbMatch[1], rgbMatch[2], rgbMatch[3]].map((n) =>
            Math.max(0, Math.min(255, Number(n))),
        );
        return [r, g, b]
            .map((n) => n.toString(16).padStart(2, '0'))
            .join('')
            .toUpperCase();
    }

    const namedColorMap: Record<string, string> = {
        red: 'FF0000',
        green: '008000',
        black: '000000',
        white: 'FFFFFF',
    };
    return namedColorMap[value];
}

function isBoldElement(element: HTMLElement): boolean {
    if (element.tagName === 'STRONG' || element.tagName === 'B') return true;
    const fontWeight = window.getComputedStyle(element).fontWeight;
    if (fontWeight === 'bold') return true;
    const weightNum = Number.parseInt(fontWeight, 10);
    return !Number.isNaN(weightNum) && weightNum >= 600;
}

function extractStyledRuns(container: HTMLElement): EditablePptRun[] {
    const runs: EditablePptRun[] = [];
    const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT);
    let currentNode: Node | null = walker.nextNode();

    while (currentNode) {
        const parent = currentNode.parentElement;
        const rawText = currentNode.textContent ?? '';

        if (parent && rawText.replace(/\s+/g, '').length > 0) {
            const text = rawText.replace(/\u00A0/g, ' ').replace(/\s+/g, ' ');
            const color = colorToPptHex(window.getComputedStyle(parent).color);
            const bold = isBoldElement(parent);

            const run: EditablePptRun = { text };
            if (color) run.color = color;
            if (bold) run.bold = true;

            const prevRun = runs[runs.length - 1];
            if (
                prevRun &&
                prevRun.color === run.color &&
                Boolean(prevRun.bold) === Boolean(run.bold)
            ) {
                prevRun.text += run.text;
            } else {
                runs.push(run);
            }
        }

        currentNode = walker.nextNode();
    }

    return runs;
}

function getKeyTakeawayLines(card: HTMLElement): EditablePptLine[] {
    const takeawaysRoot = card.querySelector<HTMLElement>('[data-key-takeaways]');
    if (!takeawaysRoot) return [];

    const listItems = Array.from(takeawaysRoot.querySelectorAll<HTMLLIElement>('li'));
    return listItems
        .map((item) => ({
            bullet: true,
            runs: extractStyledRuns(item),
        }))
        .filter((line) => line.runs.some((run) => normalizeText(run.text).length > 0));
}

function getKeyFindingLines(card: HTMLElement): EditablePptLine[] {
    const findingsRoot = card.querySelector<HTMLElement>('[data-key-findings]');
    if (!findingsRoot) return [];

    const listItems = Array.from(findingsRoot.querySelectorAll<HTMLLIElement>('li'));
    return listItems
        .map((item) => ({
            bullet: true,
            runs: extractStyledRuns(item),
        }))
        .filter((line) => line.runs.some((run) => normalizeText(run.text).length > 0));
}

function getAnalysisSummaryLines(card: HTMLElement): EditablePptLine[] {
    const summaryRoot = card.querySelector<HTMLElement>('[data-analysis-summary]');
    if (!summaryRoot) return [];

    const lines: EditablePptLine[] = [];
    const orderedBlocks = Array.from(summaryRoot.querySelectorAll<HTMLElement>('h4, li, div')).filter((el) => {
        if (el.tagName !== 'DIV') return true;
        return typeof el.className === 'string' && el.className.includes('bg-[#F5F5F5]');
    });

    orderedBlocks.forEach((block) => {
        const runs = extractStyledRuns(block);
        if (runs.length === 0) return;

        if (block.tagName === 'LI') {
            lines.push({ bullet: true, runs });
            return;
        }

        if (block.tagName === 'H4') {
            lines.push({ heading: true, runs });
            return;
        }

        lines.push({ runs });
    });

    return lines;
}

function addEditableLinesSlide(
    pptx: PptxGenJS,
    title: string,
    lines: EditablePptLine[],
    logoDataUrl: string,
    notesScope?: HTMLElement,
): PptxGenJS.Slide {
    const slide = pptx.addSlide();
    slide.background = { color: 'FFFFFF' };

    let textY = CONTENT_Y;
    slide.addText(title, {
        x: CONTENT_X,
        y: textY,
        w: CONTENT_W,
        h: 0.6,
        fontFace: EXPORT_FONT_PRIMARY,
        fontSize: 24,
        bold: true,
        color: '18181B',
        align: 'left',
        valign: 'top',
    });
    textY += 0.75;

    const richTextRuns: Array<{ text: string; options?: Record<string, unknown> }> = [];
    lines.forEach((line, lineIndex) => {
        if (line.bullet) {
            richTextRuns.push({ text: '• ', options: { color: '18181B' } });
        }

        line.runs.forEach((run, runIndex) => {
            const isLastRunInLine = runIndex === line.runs.length - 1;
            richTextRuns.push({
                text: run.text,
                options: {
                    color: run.color,
                    bold: line.heading || run.bold,
                    breakLine: isLastRunInLine,
                },
            });
        });

        if (lineIndex < lines.length - 1) {
            richTextRuns.push({ text: '', options: { breakLine: true } });
        }
    });

    slide.addText(richTextRuns as any, {
        x: CONTENT_X,
        y: textY,
        w: CONTENT_W,
        h: CONTENT_H - (textY - CONTENT_Y),
        fontFace: EXPORT_FONT_PRIMARY,
        fontSize: 16,
        color: '18181B',
        align: 'left',
        valign: 'top',
        margin: 0,
        fit: 'shrink',
    } as any);

    addPptLogo(slide, logoDataUrl);
    if (notesScope) {
        addPptSpeakerNotesFromScope(slide, notesScope);
    }
    return slide;
}

function getPreferredTextSlideTitle(card: HTMLElement): string | undefined {
    const taskContainer = card.closest<HTMLElement>('[data-task-id]');
    const taskTitle = taskContainer?.getAttribute(PPT_SLIDE_TITLE_ATTR)?.trim();
    if (taskTitle) return taskTitle;

    const cardTitle = card.getAttribute(PPT_SLIDE_TITLE_ATTR)?.trim();
    if (cardTitle) return cardTitle;

    return undefined;
}

/**
 * Render Summary/Key Takeaways as editable text in PPT instead of a captured image.
 */
function addEditableTextSlide(
    pptx: PptxGenJS,
    card: HTMLElement,
    logoDataUrl: string,
): PptxGenJS.Slide | null {
    const notesScope = getExportScopeForSlideCard(card);
    const keyTakeawayLines = getKeyTakeawayLines(card);
    if (keyTakeawayLines.length > 0) {
        const title = getPreferredTextSlideTitle(card) || 'Key Takeaways';
        return addEditableLinesSlide(pptx, title, keyTakeawayLines, logoDataUrl, notesScope);
    }

    const keyFindingLines = getKeyFindingLines(card);
    if (keyFindingLines.length > 0) {
        const title = getPreferredTextSlideTitle(card) || 'Key Findings';
        return addEditableLinesSlide(pptx, title, keyFindingLines, logoDataUrl, notesScope);
    }

    const summaryLines = getAnalysisSummaryLines(card);
    const lines = summaryLines;
    if (lines.length === 0) return null;

    const title = getPreferredTextSlideTitle(card) || 'Analysis Summary';
    return addEditableLinesSlide(pptx, title, lines, logoDataUrl, notesScope);
}

/** True when no later slide card belongs to the same right-panel question block. */
function isLastSlideCardForBlock(card: HTMLElement, allCards: HTMLElement[], index: number): boolean {
    const block = getRightPanelBlockForPart(card);
    if (!block) return false;
    for (let i = index + 1; i < allCards.length; i++) {
        if (getRightPanelBlockForPart(allCards[i]) === block) return false;
    }
    return true;
}

/** Per-question (runId) hyperlink placed bottom-left of the last slide of a question in PPT. */
function addPptRunDeepLinkFooter(slide: PptxGenJS.Slide, block: HTMLElement | null): void {
    const url = readRunDeepLinkUrlFromBlock(block);
    if (!url) return;
    slide.addText(EXPORT_RUN_DEEPLINK_LABEL, {
        x: CONTENT_X,
        y: LOGO_Y + (LOGO_H - PPT_TABLE_FOOTER_LINE_H) / 2,
        w: CONTENT_W - LOGO_W - 0.2,
        h: PPT_TABLE_FOOTER_LINE_H,
        fontFace: EXPORT_FONT_PRIMARY,
        fontSize: EXPORT_RUN_DEEPLINK_PPT_FONT_SIZE,
        color: PPT_TABLE_FOOTER_LINK_COLOR,
        underline: { style: 'sng' },
        hyperlink: { url },
        align: 'left',
        valign: 'middle',
        margin: 0,
    });
}

/** Convert styled lines (same shape as PPT export) into Word paragraphs with editable text. */
function editablePptLinesToDocxParagraphs(lines: EditablePptLine[]): Paragraph[] {
    const paragraphs: Paragraph[] = [];
    const BODY_SIZE = 22; // 11pt
    const HEADING_SIZE = DOCX_HEADING_SIZE_HALF_PT[HeadingLevel.HEADING_3] ?? 24;

    for (const line of lines) {
        const children: TextRun[] = [];
        const runSize = line.heading ? HEADING_SIZE : BODY_SIZE;

        if (line.bullet) {
            children.push(new TextRun({ text: '• ', font: EXPORT_FONT_PRIMARY, size: BODY_SIZE }));
        }
        for (const run of line.runs) {
            const t = run.text;
            if (!normalizeText(t)) continue;
            const colorHex = run.color ? run.color.replace(/^#/, '').toUpperCase() : undefined;
            children.push(
                new TextRun({
                    text: t,
                    bold: Boolean(line.heading || run.bold),
                    ...(colorHex && colorHex.length === 6 ? { color: colorHex } : {}),
                    font: EXPORT_FONT_PRIMARY,
                    size: runSize,
                }),
            );
        }
        if (children.length === 0) continue;

        if (line.heading) {
            paragraphs.push(
                new Paragraph({
                    children,
                    heading: HeadingLevel.HEADING_3,
                    spacing: { before: 120, after: 80 },
                }),
            );
        } else {
            paragraphs.push(
                new Paragraph({
                    children,
                    spacing: { after: 80 },
                }),
            );
        }
    }
    return paragraphs;
}

/**
 * When a slide card is key takeaways, key findings, or analysis summary, return Word paragraphs
 * instead of rasterizing (charts/tables still export as images).
 */
function addEditableDocxParagraphsFromCard(card: HTMLElement): Paragraph[] | null {
    const keyTakeawayLines = getKeyTakeawayLines(card);
    if (keyTakeawayLines.length > 0) {
        return editablePptLinesToDocxParagraphs(keyTakeawayLines);
    }

    const keyFindingLines = getKeyFindingLines(card);
    if (keyFindingLines.length > 0) {
        return editablePptLinesToDocxParagraphs(keyFindingLines);
    }

    const summaryLines = getAnalysisSummaryLines(card);
    if (summaryLines.length > 0) {
        return editablePptLinesToDocxParagraphs(summaryLines);
    }

    return null;
}

async function addContainerAsSlidesToPPTX(
    pptx: PptxGenJS,
    container: HTMLElement,
    logoDataUrl: string,
): Promise<void> {
    const slideCards = getSlideCardsForContainer(container);

    if (slideCards.length > 0) {
        // Per-card mode: each marked/leaf card becomes a slide.
        for (let cardIndex = 0; cardIndex < slideCards.length; cardIndex++) {
            const card = slideCards[cardIndex];
            const isLastCardOfBlock = isLastSlideCardForBlock(card, slideCards, cardIndex);

            const editableSlide = addEditableTextSlide(pptx, card, logoDataUrl);
            if (editableSlide) {
                if (isLastCardOfBlock) {
                    addPptRunDeepLinkFooter(editableSlide, getRightPanelBlockForPart(card));
                }
                continue;
            }

            const canvas = await captureElementAsCanvasForPPT(card);
            if (!canvas) continue;

            const slide = pptx.addSlide();
            slide.background = { color: 'FFFFFF' };
            const footerHeight = getPptTableFooterBlockHeight(card);
            const maxImageH =
                footerHeight > 0
                    ? CONTENT_H - footerHeight - PPT_TABLE_FOOTER_GAP
                    : CONTENT_H;
            const placement = addCanvasToSlide(slide, canvas, maxImageH);
            if (footerHeight > 0) {
                addPptTableExportFooter(
                    slide,
                    card,
                    placement.imgX,
                    placement.imgY + placement.imgH + PPT_TABLE_FOOTER_GAP,
                    placement.imgW,
                );
            }
            addPptLogo(slide, logoDataUrl);
            addPptSpeakerNotesFromScope(slide, getExportScopeForSlideCard(card));
            if (isLastCardOfBlock) {
                addPptRunDeepLinkFooter(slide, getRightPanelBlockForPart(card));
            }
        }
        return;
    }

    // No explicit slide markers on this export target. If it is a text-only
    // summary/takeaways block, export as editable text instead of image.
    if (addEditableTextSlide(pptx, container, logoDataUrl)) return;

    // Fallback: slice whole container into multiple slides.
    const canvas = await captureElementAsCanvasForPPT(container);
    if (!canvas) {
        console.error('Failed to capture content for PPTX');
        return;
    }

    const pxPerInch = canvas.width / CONTENT_W;
    const sliceHeightPx = Math.floor(CONTENT_H * pxPerInch);
    const pages = sliceCanvasIntoPages(canvas, sliceHeightPx);

    const footerHeight = getPptTableFooterBlockHeight(container);
    const maxImageH =
        footerHeight > 0 ? CONTENT_H - footerHeight - PPT_TABLE_FOOTER_GAP : CONTENT_H;

    for (let pageIndex = 0; pageIndex < pages.length; pageIndex++) {
        const page = pages[pageIndex];
        const offscreen = document.createElement('canvas');
        offscreen.width = page.width;
        offscreen.height = page.height;
        const ctx = offscreen.getContext('2d');
        if (!ctx) continue;

        const img = new Image();
        await new Promise<void>((res) => {
            img.onload = () => {
                ctx.drawImage(img, 0, 0);
                res();
            };
            img.src = page.dataUrl;
        });

        const slide = pptx.addSlide();
        slide.background = { color: 'FFFFFF' };
        const placement = addCanvasToSlide(slide, offscreen, maxImageH);
        const isLastPage = pageIndex === pages.length - 1;
        if (isLastPage && footerHeight > 0) {
            addPptTableExportFooter(
                slide,
                container,
                placement.imgX,
                placement.imgY + placement.imgH + PPT_TABLE_FOOTER_GAP,
                placement.imgW,
            );
        }
        addPptLogo(slide, logoDataUrl);
        addPptSpeakerNotesFromScope(slide, container);
    }
}

/**
 * Export a container element to PowerPoint (PPTX).
 *
 * - If the container has children marked with [data-export-slide], each child
 *   is captured individually and placed on its own slide (one card = one slide).
 * - Otherwise the full container is captured and sliced into page-height chunks.
 *
 * The Asper logo is placed in the bottom-right corner of every slide.
 */
export async function exportContainerToPPTX(
    container: HTMLElement | null,
    options?: PptExportOptions,
): Promise<void> {
    if (!container) {
        console.error('Export container is null');
        return;
    }

    try {
        await new Promise((resolve) => setTimeout(resolve, 500));

        const pptx = new PptxGenJS();
        pptx.layout = 'LAYOUT_WIDE';

        const logoDataUrl = await svgToPngDataUrl(ASPER_LOGO_SVG, 94, 32);
        if (options?.coverSlide) {
            addPptCoverSlide(pptx, logoDataUrl, options.coverSlide);
        }

        await addContainerAsSlidesToPPTX(pptx, container, logoDataUrl);

        const filename = options?.filename || `${buildAskAsperExportBasename(options?.title || 'artifact')}.pptx`;

        await pptx.writeFile({ fileName: filename });
    } catch (error) {
        console.error('Error exporting to PPTX:', error);
        throw error;
    }
}

/**
 * Export multiple HTML elements to a single PowerPoint (PPTX).
 *
 * Each element gets its own slide. The Asper logo is placed in the
 * bottom-right corner of every slide.
 */
export async function exportMultipleElementsToPPTX(
    elements: (HTMLElement | null)[],
    options?: PptExportOptions,
): Promise<void> {
    const validElements = elements.filter((el): el is HTMLElement => el !== null);
    if (validElements.length === 0) {
        console.error('No valid elements to export to PPTX');
        return;
    }

    try {
        await new Promise((resolve) => setTimeout(resolve, 500));

        const pptx = new PptxGenJS();
        pptx.layout = 'LAYOUT_WIDE';

        const logoDataUrl = await svgToPngDataUrl(ASPER_LOGO_SVG, 94, 32);
        if (options?.coverSlide) {
            addPptCoverSlide(pptx, logoDataUrl, options.coverSlide);
        }

        for (const element of validElements) {
            await addContainerAsSlidesToPPTX(pptx, element, logoDataUrl);
        }

        const filename = options?.filename || `${buildAskAsperExportBasename(options?.title || 'artifact')}.pptx`;

        await pptx.writeFile({ fileName: filename });
    } catch (error) {
        console.error('Error exporting multiple elements to PPTX:', error);
        throw error;
    }
}
